import React, { useState, useRef, useCallback, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { tiendaDb } from '../../../lib/supabase';
import { calcularPvp, calcularPvpPsicologico, calcularCosteLinea, getMargenAplicable, MARGEN_DEFAULT, REGLA_REDONDEO_DEFAULT,  } from '../../../utils/precioCalculation';
import { confirmarFacturaYEntradaMercancia } from '../../../services/purchaseInvoiceService';
import { processInvoice, buildManualInvoiceResult } from '../../../services/ocr/ocrInvoiceService';
import { generarCodigoInterno } from '../../../services/productService';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const RED = '#F87171';
const AMBER = '#FBBF24';
const PURPLE = '#A78BFA';
const BUCKET = 'facturas-compras';
const SIDEBAR_W = 280;

const fmt = (n, d = 2) => typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => typeof n === 'number' ? `${fmt(n)} €` : '—';
const fmtPct = (n) => typeof n === 'number' ? `${(n * 100)?.toFixed(1)} %` : '—';

// ─── AUTO-CATEGORIZATION MAP ──────────────────────────────────────────────────
const CATEGORY_KEYWORDS = [
  { keywords: ['jamón', 'jamon', 'jam ', 'paleta', 'pata negra', 'ibérico', 'iberico', 'bellota', 'cebo', 'serrano'], categoria: 'Ibéricos', subcategoria: 'Jamones' },
  { keywords: ['lomo', 'chorizo', 'salchichón', 'salchichon', 'fuet', 'morcilla', 'butifarra', 'sobrasada', 'mortadela', 'embutido'], categoria: 'Embutidos', subcategoria: 'Embutidos curados' },
  { keywords: ['queso', 'manchego', 'brie', 'camembert', 'gouda', 'roquefort', 'parmesano'], categoria: 'Quesos', subcategoria: 'Quesos' },
  { keywords: ['aceite', 'aove', 'oliva', 'girasol'], categoria: 'Aceites', subcategoria: 'Aceites' },
  { keywords: ['conserva', 'atún', 'atun', 'sardina', 'mejillón', 'mejillon', 'anchoa', 'berberecho'], categoria: 'Conservas', subcategoria: 'Conservas de pescado' },
  { keywords: ['café', 'cafe', 'espresso', 'capuchino', 'descafeinado'], categoria: 'Cafés', subcategoria: 'Cafés' },
  { keywords: ['yogur', 'yogurt', 'leche', 'mantequilla', 'nata', 'crema', 'lácteo', 'lacteo'], categoria: 'Lácteos', subcategoria: 'Lácteos' },
  { keywords: ['pan', 'barra', 'hogaza', 'baguette', 'chapata', 'molde', 'tostada', 'galleta', 'bollería', 'bolleria'], categoria: 'Panadería', subcategoria: 'Pan y bollería' },
  { keywords: ['vino', 'rioja', 'ribera', 'albariño', 'cava', 'champán', 'champan', 'cerveza', 'sidra', 'licor', 'whisky', 'ron', 'vodka', 'gin'], categoria: 'Bebidas', subcategoria: 'Bebidas alcohólicas' },
  { keywords: ['agua', 'refresco', 'zumo', 'naranjada', 'limonada', 'cola', 'tónica', 'tonica'], categoria: 'Bebidas', subcategoria: 'Bebidas sin alcohol' },
  { keywords: ['miel', 'mermelada', 'confitura', 'paté', 'pate', 'foie', 'trufa'], categoria: 'Gourmet', subcategoria: 'Productos gourmet' },
  { keywords: ['vinagre', 'sal', 'pimienta', 'especias', 'condimento', 'salsa', 'mostaza', 'ketchup'], categoria: 'Condimentos', subcategoria: 'Condimentos y salsas' },
  { keywords: ['fruta', 'verdura', 'hortaliza', 'tomate', 'lechuga', 'zanahoria', 'cebolla', 'ajo', 'patata'], categoria: 'Frescos', subcategoria: 'Frutas y verduras' },
  { keywords: ['carne', 'ternera', 'cerdo', 'pollo', 'cordero', 'pavo', 'conejo', 'filete', 'chuleta', 'costilla'], categoria: 'Carnes', subcategoria: 'Carnes frescas' },
  { keywords: ['pescado', 'merluza', 'salmón', 'salmon', 'bacalao', 'dorada', 'lubina', 'rape', 'gambas', 'langostino', 'marisco'], categoria: 'Pescados', subcategoria: 'Pescados y mariscos' },
  { keywords: ['pasta', 'arroz', 'legumbre', 'lenteja', 'garbanzo', 'alubia', 'macarrón', 'macarron', 'espagueti'], categoria: 'Alimentación seca', subcategoria: 'Pasta y cereales' },
  { keywords: ['chocolate', 'cacao', 'bombón', 'bombon', 'caramelo', 'dulce', 'turrón', 'turron', 'mazapán', 'mazapan'], categoria: 'Dulces', subcategoria: 'Chocolates y dulces' },
  { keywords: ['limpieza', 'detergente', 'jabón', 'jabon', 'lejía', 'lejia', 'fregasuelos', 'bayeta', 'estropajo'], categoria: 'Limpieza', subcategoria: 'Productos de limpieza' },
  { keywords: ['bolsa', 'envase', 'embalaje', 'caja', 'papel', 'film', 'aluminio', 'servilleta'], categoria: 'Embalajes', subcategoria: 'Material de embalaje' },
];

const suggestCategory = (descripcion) => {
  if (!descripcion) return { categoria: null, subcategoria: null };
  const desc = descripcion?.toLowerCase();
  for (const entry of CATEGORY_KEYWORDS) {
    if (entry?.keywords?.some(kw => desc?.includes(kw))) {
      return { categoria: entry?.categoria, subcategoria: entry?.subcategoria };
    }
  }
  return { categoria: null, subcategoria: null };
};

// ─── TEXT NORMALIZATION ───────────────────────────────────────────────────────
const normalizeText = (text) => {
  if (!text) return '';
  return text
    ?.toUpperCase()
    ?.normalize('NFD')
    ?.replace(/[\u0300-\u036f]/g, '')
    ?.replace(/\s+/g, '')
    ?.replace(/\./g, '')
    ?.replace(/-/g, '')
    ?.replace(/,/g, '')
    ?.replace(/S\.?L\.?$/i, 'SL')
    ?.replace(/S\.?A\.?$/i, 'SA')
    ?.replace(/S\.?L\.?U\.?$/i, 'SLU')
    ?.replace(/S\.?C\.?$/i, 'SC')
    ?.trim();
};

const stringSimilarity = (a, b) => {
  if (!a || !b) return 0;
  const na = normalizeText(a);
  const nb = normalizeText(b);
  if (na === nb) return 100;
  if (na?.includes(nb) || nb?.includes(na)) return 85;
  const wordsA = na?.split(/\s+/)?.filter(w => w?.length > 2) || [];
  const wordsB = nb?.split(/\s+/)?.filter(w => w?.length > 2) || [];
  if (!wordsA?.length || !wordsB?.length) return 0;
  const matches = wordsA?.filter(w => wordsB?.includes(w))?.length;
  return Math.round((matches / Math.max(wordsA?.length, wordsB?.length)) * 100);
};

// ─── DATE NORMALIZATION ───────────────────────────────────────────────────────
/**
 * Normalizes any date value to YYYY-MM-DD.
 * Accepts: YYYY-MM-DD, DD/MM/YYYY, DD/MM/YY, DD-MM-YYYY, DD-MM-YY, DD.MM.YYYY, DD.MM.YY, Date objects.
 * Returns null if invalid.
 */
const normalizeInvoiceDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    if (isNaN(value?.getTime())) return null;
    const y = value?.getFullYear();
    const m = String(value?.getMonth() + 1)?.padStart(2, '0');
    const d = String(value?.getDate())?.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const str = String(value)?.trim();
  if (!str) return null;
  let day, month, year;
  const isoMatch = str?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    year = parseInt(isoMatch?.[1], 10);
    month = parseInt(isoMatch?.[2], 10);
    day = parseInt(isoMatch?.[3], 10);
  } else {
    const dmyMatch = str?.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})$/);
    if (!dmyMatch) return null;
    day = parseInt(dmyMatch?.[1], 10);
    month = parseInt(dmyMatch?.[2], 10);
    let rawYear = parseInt(dmyMatch?.[3], 10);
    if (dmyMatch?.[3]?.length === 2) rawYear = rawYear <= 69 ? 2000 + rawYear : 1900 + rawYear;
    year = rawYear;
  }
  if (!day || !month || !year) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > 2100) return null;
  const daysInMonth = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  if (month === 2 && isLeap) daysInMonth[2] = 29;
  if (day > daysInMonth?.[month]) return null;
  const mm = String(month)?.padStart(2, '0');
  const dd = String(day)?.padStart(2, '0');
  return `${year}-${mm}-${dd}`;
};

const PROCESSING_STEPS = [
  { id: 'upload', label: 'Subiendo documento...', icon: 'Upload' },
  { id: 'ai', label: 'Procesando con OpenAI...', icon: 'Brain' },
  { id: 'normalize', label: 'Normalizando datos...', icon: 'ScanLine' },
  { id: 'proveedor', label: 'Detectando proveedor...', icon: 'Building2' },
  { id: 'lineas', label: 'Relacionando productos...', icon: 'Search' },
  { id: 'ready', label: 'Preparando revisión...', icon: 'CheckCircle2' },
];

const CONFIDENCE_CONFIG = {
  VERDE:    { color: ACCENT,  label: 'Coincidencia segura',   icon: 'CheckCircle2',  bg: `${ACCENT}08` },
  AMARILLO: { color: AMBER,   label: 'Revisar',               icon: 'AlertCircle',   bg: `${AMBER}08`  },
  ROJO:     { color: RED,     label: 'No encontrado',         icon: 'XCircle',       bg: `${RED}08`    },
};

// ─── STEP INDICATOR ──────────────────────────────────────────────────────────
const StepIndicator = ({ currentStep }) => {
  const steps = [
    { n: 1, label: 'Subir' },
    { n: 2, label: 'Procesar' },
    { n: 3, label: 'Revisar' },
    { n: 4, label: 'Guardar' },
  ];
  return (
    <div className="flex items-center justify-center gap-0">
      {steps?.map((s, i) => (
        <React.Fragment key={s?.n}>
          <div className="flex flex-col items-center gap-1">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all"
              style={currentStep >= s?.n
                ? { background: `${BLUE}25`, border: `2px solid ${BLUE}`, color: BLUE }
                : { background: 'rgba(255,255,255,0.04)', border: '2px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.30)' }}>
              {currentStep > s?.n ? <Icon name="Check" size={11} /> : s?.n}
            </div>
            <span className="font-oranienbaum text-[9px] hidden sm:block"
              style={{ color: currentStep >= s?.n ? 'rgba(255,255,255,0.70)' : 'rgba(255,255,255,0.25)' }}>
              {s?.label}
            </span>
          </div>
          {i < steps?.length - 1 && (
            <div className="w-6 sm:w-10 h-px mx-1 mb-3"
              style={{ background: currentStep > s?.n ? `${BLUE}50` : 'rgba(255,255,255,0.10)' }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const ProvisionalBadge = ({ type }) => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider"
    style={{ background: `${AMBER}15`, color: AMBER, border: `1px solid ${AMBER}30` }}>
    <Icon name="Clock" size={7} />
    {type === 'proveedor' ? 'PENDIENTE' : 'PROVISIONAL'}
  </span>
);

// ─── PRICE PREVIEW PANEL ─────────────────────────────────────────────────────
const PricePreview = ({ costeConImpuestos, ivaPct, margen, reglaRedondeo }) => {
  if (!costeConImpuestos || costeConImpuestos <= 0) return null;
  const { pvpSinIva, pvpConIva, margenReal } = calcularPvp(costeConImpuestos, margen, ivaPct);
  const pvpPsico = calcularPvpPsicologico(pvpConIva, reglaRedondeo);
  return (
    <div className="mt-1 p-2 rounded-lg space-y-1" style={{ background: `${BLUE}08`, border: `1px solid ${BLUE}15` }}>
      <div className="flex justify-between text-[9px] font-oranienbaum">
        <span style={{ color: 'rgba(255,255,255,0.40)' }}>Coste c/imp.</span>
        <span style={{ color: 'rgba(255,255,255,0.70)' }}>{fmtEur(costeConImpuestos)}</span>
      </div>
      <div className="flex justify-between text-[9px] font-oranienbaum">
        <span style={{ color: 'rgba(255,255,255,0.40)' }}>Margen {(margen * 100)?.toFixed(0)}%</span>
        <span style={{ color: BLUE }}>{fmtEur(pvpSinIva)} s/IVA</span>
      </div>
      <div className="flex justify-between text-[9px] font-oranienbaum">
        <span style={{ color: 'rgba(255,255,255,0.40)' }}>PVP teórico</span>
        <span style={{ color: BLUE }}>{fmtEur(pvpConIva)}</span>
      </div>
      <div className="flex justify-between text-[9px] font-oranienbaum font-bold">
        <span style={{ color: ACCENT }}>PVP recomendado</span>
        <span style={{ color: ACCENT }}>{fmtEur(pvpPsico)}</span>
      </div>
      <div className="flex justify-between text-[9px] font-oranienbaum">
        <span style={{ color: 'rgba(255,255,255,0.30)' }}>Margen real</span>
        <span style={{ color: margenReal >= 0.30 ? ACCENT : AMBER }}>{fmtPct(margenReal)}</span>
      </div>
    </div>
  );
};

// ─── MAIN WIZARD ─────────────────────────────────────────────────────────────
const ImportFacturaWizard = ({ proveedores, productos, onClose, onSaved }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Step 2: processing
  const [processingStep, setProcessingStep] = useState(0);
  const [processingError, setProcessingError] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);

  // Step 3: review
  const [reviewLines, setReviewLines] = useState([]);
  const [reviewCabecera, setReviewCabecera] = useState(null);
  const [proveedorInfo, setProveedorInfo] = useState(null);
  const [validationWarning, setValidationWarning] = useState(null);
  const [processingLog, setProcessingLog] = useState([]);

  // Global settings for pricing
  const [margenDefault, setMargenDefault] = useState(MARGEN_DEFAULT);
  const [reglaRedondeo, setReglaRedondeo] = useState(REGLA_REDONDEO_DEFAULT);
  const [showPricingSettings, setShowPricingSettings] = useState(false);

  // Step 4: saving
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedFactura, setSavedFactura] = useState(null);
  const [saveResult, setSaveResult] = useState(null);

  // Product selector modal
  const [selectorLine, setSelectorLine] = useState(null);
  const [selectorSearch, setSelectorSearch] = useState('');

  // New product form modal
  const [newProductLine, setNewProductLine] = useState(null);
  const [newProductForm, setNewProductForm] = useState({});
  // Familias para el selector obligatorio del nuevo producto
  const [familiasProducto, setFamiliasProducto] = useState([]);

  // Inventory state (for stock preview)
  const [inventarioActual, setInventarioActual] = useState({});

  // Manual entry mode
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualForm, setManualForm] = useState({
    proveedor: '',
    cif: '',
    numero_factura: '',
    fecha: '',
    fecha_vencimiento: '',
    forma_pago: '',
    base_imponible: '',
    iva: '',
    total: '',
    observaciones: '',
    lineas: [{ descripcion: '', referencia_proveedor: '', ean: '', cantidad: '', unidad: 'UNIDAD', precio_unitario: '', descuento: '', descuento_porcentaje: '', porcentaje_iva: '10' }],
  });

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const addLog = (msg) => setProcessingLog(prev => [...prev, msg]);

  // Load current inventory for stock preview
  useEffect(() => {
    const loadInventario = async () => {
      try {
        const { data } = await tiendaDb?.from('inventario_actual')?.select('producto_id, stock_actual');
        const map = {};
        (data || [])?.forEach(i => { map[i?.producto_id] = i?.stock_actual || 0; });
        setInventarioActual(map);
      } catch {}
    };
    loadInventario();
  }, []);

  // ─── RECALCULATE PVP FOR ALL LINES ───────────────────────────────────────
  const recalcularTodosPvp = () => {
    setReviewLines(prev => prev?.map(l => {
      const precioUnitario = parseFloat(l?.precio_unitario) || 0;
      const descuento = parseFloat(l?.descuento) || 0;
      const descPct = parseFloat(l?.descuento_porcentaje) || 0;
      const ivaPct = parseFloat(l?.porcentaje_iva) || 10;
      const recargoEquivalenciaPct = parseFloat(l?.producto_sugerido?.recargo_equivalencia_pct) || 0;
      const { costeNeto, costeConIva, costeConImpuestos } = calcularCosteLinea({
        precioUnitario,
        descuentoImporte: descuento,
        descuentoPorcentaje: descPct,
        ivaPct,
        recargoEquivalenciaPct,
      });
      const margen = l?.margen || margenDefault;
      // PVP and margin calculated on coste_con_impuestos (real cost borne by the store)
      const { pvpConIva, pvpSinIva, margenReal } = calcularPvp(costeConImpuestos, margen, ivaPct);
      const pvpPsico = calcularPvpPsicologico(pvpConIva, reglaRedondeo);
      return {
        ...l,
        coste_neto: costeNeto,
        coste_con_iva: costeConIva,
        coste_con_impuestos: costeConImpuestos,
        pvp_teorico: pvpConIva,
        pvp_recomendado: pvpPsico,
        pvp_sin_iva: pvpSinIva,
        margen_real: margenReal,
      };
    }));
  };

  // ─── FILE HANDLING ────────────────────────────────────────────────────────
  const handleFile = (f) => {
    if (!f) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed?.includes(f?.type)) {
      alert('Formato no soportado. Usa JPG, PNG, WEBP o PDF.');
      return;
    }
    setFile(f);
    if (f?.type?.startsWith('image/')) {
      const url = URL.createObjectURL(f);
      setPreview(url);
    } else {
      setPreview('pdf');
    }
  };

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    setIsDragging(false);
    const dropped = e?.dataTransfer?.files?.[0];
    if (dropped) handleFile(dropped);
  }, []);

  const handleDragOver = (e) => { e?.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  // ─── PROVIDER MATCHING ────────────────────────────────────────────────────
  const matchProvider = async (cabecera) => {
    const provNombre = cabecera?.proveedor || '';
    const provCif = cabecera?.cif || '';
    addLog(`Buscando proveedor: "${provNombre}" CIF: "${provCif}"`);
    let matched = null;
    let matchMethod = null;
    if (provCif) {
      const normCif = normalizeText(provCif);
      matched = proveedores?.find(p => normalizeText(p?.nif_cif || '') === normCif);
      if (matched) matchMethod = 'CIF/NIF';
    }
    if (!matched && provNombre) {
      const normNombre = normalizeText(provNombre);
      matched = proveedores?.find(p => normalizeText(p?.nombre || '') === normNombre);
      if (matched) matchMethod = 'Nombre fiscal exacto';
    }
    if (!matched && provNombre) {
      const normNombre = normalizeText(provNombre);
      matched = proveedores?.find(p => normalizeText(p?.nombre_comercial || '') === normNombre);
      if (matched) matchMethod = 'Nombre comercial';
    }
    if (!matched && provNombre) {
      let bestScore = 0;
      let bestProv = null;
      proveedores?.forEach(p => {
        const score = Math.max(
          stringSimilarity(provNombre, p?.nombre || ''),
          stringSimilarity(provNombre, p?.nombre_comercial || '')
        );
        if (score > bestScore) { bestScore = score; bestProv = p; }
      });
      if (bestScore >= 80) { matched = bestProv; matchMethod = `Similitud ${bestScore}%`; }
    }
    if (matched) {
      addLog(`✓ Proveedor encontrado: "${matched?.nombre}" (${matchMethod})`);
      return { id: matched?.id, nombre: matched?.nombre, isNew: false, isProvisional: false, data: matched };
    }
    addLog(`⚠ Proveedor no encontrado. Creando provisional: "${provNombre}"`);
    const provisionData = { nombre: provNombre || 'PROVEEDOR DESCONOCIDO', nif_cif: provCif || null, activo: true };
    try {
      const { data: newProv, error } = await tiendaDb?.from('proveedores')?.insert(provisionData)?.select()?.single();
      if (!error && newProv) {
        addLog(`✓ Proveedor provisional creado: ID ${newProv?.id}`);
        return { id: newProv?.id, nombre: newProv?.nombre, isNew: true, isProvisional: true, data: newProv };
      }
    } catch (e) {
      addLog(`⚠ No se pudo crear proveedor provisional: ${e?.message}`);
    }
    return { id: null, nombre: provNombre, isNew: true, isProvisional: true, data: null };
  };

  // ─── PRODUCT MATCHING ─────────────────────────────────────────────────────
  const matchProduct = async (linea, proveedorId, mappings) => {
    let producto = null;
    let confianza = 0;
    let estado = 'ROJO';
    let matchMethod = null;

    // 1. EAN exacto
    if (linea?.ean) {
      const byEan = productos?.find(p => p?.ean && normalizeText(p?.ean) === normalizeText(linea?.ean));
      if (byEan) { producto = byEan; confianza = 99; estado = 'VERDE'; matchMethod = 'EAN'; }
    }
    // 2. Referencia proveedor — SOLO mediante ocr_product_mappings con proveedor específico
    if (!producto && linea?.referencia_proveedor && proveedorId) {
      const byMappingRef = mappings?.find(m =>
        m?.proveedor_id === proveedorId &&
        m?.referencia_proveedor &&
        normalizeText(m?.referencia_proveedor) === normalizeText(linea?.referencia_proveedor || '')
      );
      if (byMappingRef) {
        const prod = productos?.find(p => p?.id === byMappingRef?.producto_id);
        if (prod) { producto = prod; confianza = Math.min(byMappingRef?.confianza || 95, 99); estado = 'VERDE'; matchMethod = 'Referencia proveedor (mapping)'; }
      }
    }
    // 3. Historial OCR
    if (!producto && proveedorId) {
      const normDesc = normalizeText(linea?.descripcion_original || '');
      const byMapping = mappings?.find(m =>
        m?.proveedor_id === proveedorId &&
        normalizeText(m?.descripcion_proveedor || '') === normDesc
      );
      if (byMapping) {
        const prod = productos?.find(p => p?.id === byMapping?.producto_id);
        if (prod) { producto = prod; confianza = Math.min(byMapping?.confianza || 88, 96); estado = 'VERDE'; matchMethod = 'Historial OCR'; }
      }
    }
    // 4. Nombre normalizado exacto
    if (!producto) {
      const normDesc = normalizeText(linea?.descripcion_original || '');
      const byName = productos?.find(p => normalizeText(p?.nombre || '') === normDesc);
      if (byName) { producto = byName; confianza = 87; estado = 'VERDE'; matchMethod = 'Nombre exacto'; }
    }
    // 5. Similitud aproximada
    if (!producto) {
      const desc = linea?.descripcion_original?.toLowerCase() || '';
      const words = desc?.split(/\s+/)?.filter(w => w?.length > 3);
      let bestScore = 0;
      let bestProd = null;
      productos?.forEach(p => {
        const pName = p?.nombre?.toLowerCase() || '';
        const matches = words?.filter(w => pName?.includes(w))?.length;
        const score = words?.length > 0 ? (matches / words?.length) * 100 : 0;
        if (score > bestScore) { bestScore = score; bestProd = p; }
      });
      if (bestScore >= 55) {
        producto = bestProd;
        confianza = Math.round(bestScore * 0.60);
        estado = 'AMARILLO';
        matchMethod = `Similitud ${Math.round(bestScore)}% — Requiere revisión`;
      }
    }

    const { categoria: catSugerida, subcategoria: subSugerida } = suggestCategory(linea?.descripcion_original);

    // Validate factor_conversion against product's saved factor if available
    let factorWarning = linea?.warning_conversion || null;
    const detectedFactor = linea?.factor_conversion != null ? parseFloat(linea?.factor_conversion) : null;
    if (producto && detectedFactor != null && producto?.factor_conversion != null) {
      const savedFactor = parseFloat(producto?.factor_conversion);
      if (Math.abs(detectedFactor - savedFactor) > 0.01) {
        factorWarning = `Factor detectado (${detectedFactor}) difiere del guardado en el producto (${savedFactor}). Revisa antes de confirmar.`;
        if (estado === 'VERDE') estado = 'AMARILLO';
      }
    }

    // ── Calculate costs using real IVA from OCR line ──────────────────────
    // precio_compra_neto = precio_bruto × (1 - descuento%)
    // precio_compra_con_iva = precio_compra_neto × (1 + iva/100)
    // coste_con_impuestos = precio_compra_neto × (1 + iva/100 + recargo/100)
    const precioUnitario = parseFloat(linea?.precio_unitario) || 0;
    const descuento = parseFloat(linea?.descuento) || 0;
    const descPct = parseFloat(linea?.descuento_porcentaje) || 0;
    // Use REAL IVA from OCR line — do NOT default to 10% if OCR detected a different value
    const ivaPct = linea?.porcentaje_iva != null ? parseFloat(linea?.porcentaje_iva) : 10;
    const recargoEquivalenciaPct = parseFloat(producto?.recargo_equivalencia_pct) || 0;
    const margen = getMargenAplicable(producto, margenDefault);

    const { costeNeto, costeConIva, costeConImpuestos } = calcularCosteLinea({
      precioUnitario,
      descuentoImporte: descuento,
      descuentoPorcentaje: descPct,
      ivaPct,
      recargoEquivalenciaPct,
    });

    // PVP and margin are calculated on coste_con_impuestos (real cost borne by the store)
    const { pvpConIva, pvpSinIva, margenReal } = calcularPvp(costeConImpuestos, margen, ivaPct);
    const pvpPsico = calcularPvpPsicologico(pvpConIva, reglaRedondeo);

    return {
      _key: Math.random()?.toString(36)?.slice(2),
      ...linea,
      producto_sugerido: producto,
      producto_id: producto?.id || null,
      confianza,
      estado,
      matchMethod,
      editado: false,
      isProvisional: false,
      categoria_sugerida: catSugerida,
      subcategoria_sugerida: subSugerida,
      // Pricing — keep original OCR IVA
      porcentaje_iva: ivaPct,
      precio_bruto: precioUnitario,
      descuento_porcentaje: descPct,
      coste_neto: costeNeto,
      coste_con_iva: costeConIva,
      coste_con_impuestos: costeConImpuestos,
      margen: margen,
      pvp_teorico: pvpConIva,
      pvp_recomendado: pvpPsico,
      pvp_sin_iva: pvpSinIva,
      margen_real: margenReal,
      afecta_stock: true,
      // Logistic split — preserve from OCR
      cantidad_compra: linea?.cantidad_compra != null ? linea?.cantidad_compra : (parseFloat(linea?.cantidad) || 0),
      unidad_compra: linea?.unidad_compra || linea?.unidad || null,
      cantidad_inventario: linea?.cantidad_inventario != null ? linea?.cantidad_inventario : null,
      unidad_inventario: linea?.unidad_inventario || null,
      factor_conversion: linea?.factor_conversion != null ? linea?.factor_conversion : null,
      warning_conversion: factorWarning,
    };
  };

  // ─── STEP 2: PROCESS (OpenAI via Edge Function — no OCR.Space, no Gemini) ──
  const processFile = async () => {
    if (!file) return;
    setStep(2);
    setProcessingError(null);
    setProcessingStep(0);
    setProcessingLog([]);

    // Use ocrInvoiceService as single entry point.
    // Internally calls procesar-factura-compra Edge Function → OpenAI.
    // No OCR.Space. No Gemini. No intermediate text extraction.
    const processResult = await processInvoice(file, (stepId, msg) => {
      addLog(msg);
      const stepMap = { upload: 0, ai: 1, normalize: 2 };
      if (stepMap?.[stepId] !== undefined) setProcessingStep(stepMap?.[stepId]);
    });

    if (!processResult?.success) {
      // OpenAI failed — do NOT save anything, do NOT touch inventory
      // Show error with retry and manual entry options
      setProcessingError(processResult?.error || 'No se ha podido procesar el documento');
      return;
    }

    const normalizedResult = processResult?.result;

    try {
      const provInfo = await matchProvider(normalizedResult?.cabecera || {});
      setProveedorInfo(provInfo);
      setProcessingStep(3);

      let mappings = [];
      if (provInfo?.id) {
        const { data: mappingData } = await tiendaDb?.from('ocr_product_mappings')
          ?.select('*, producto:productos(id, nombre, codigo_interno, ean)')
          ?.eq('proveedor_id', provInfo?.id);
        mappings = mappingData || [];
        addLog(`${mappings?.length} relaciones aprendidas cargadas`);
      }

      setProcessingStep(4);
      const lineas = normalizedResult?.lineas || [];
      addLog(`Procesando ${lineas?.length} líneas...`);

      const allMatched = await Promise.all(lineas?.map(linea => matchProduct(linea, provInfo?.id, mappings)));
      addLog(`✓ ${allMatched?.filter(l => l?.estado === 'VERDE')?.length} verdes, ${allMatched?.filter(l => l?.estado === 'AMARILLO')?.length} amarillas, ${allMatched?.filter(l => l?.estado === 'ROJO')?.length} rojas`);

      setProcessingStep(5);

      const cabecera = normalizedResult?.cabecera || {};
      setReviewCabecera({
        proveedor_id: provInfo?.id || '',
        proveedor_nombre: provInfo?.nombre || cabecera?.proveedor || '',
        numero_factura: cabecera?.numero_factura || '',
        fecha_factura: normalizeInvoiceDate(cabecera?.fecha) ?? '',
        fecha_vencimiento: normalizeInvoiceDate(cabecera?.fecha_vencimiento) ?? '',
        forma_pago: cabecera?.forma_pago || '',
        base_imponible: cabecera?.base_imponible || 0,
        iva: cabecera?.iva || 0,
        total: cabecera?.total || 0,
        descuentos: cabecera?.descuentos || 0,
        portes: cabecera?.portes || 0,
        cif_proveedor: cabecera?.cif || '',
        observaciones: cabecera?.observaciones || '',
      });

      setReviewLines(allMatched);
      setOcrResult({ ...normalizedResult, storagePath: processResult?.storagePath });

      const sumLineas = allMatched?.reduce((s, l) => s + (parseFloat(l?.subtotal) || 0), 0);
      const baseFactura = parseFloat(cabecera?.base_imponible) || 0;
      const diff = Math.abs(sumLineas - baseFactura);
      if (diff > 0.05 && baseFactura > 0) {
        setValidationWarning(`Diferencia de ${fmtEur(diff)} entre la suma de líneas (${fmtEur(sumLineas)}) y la base imponible (${fmtEur(baseFactura)})`);
      }

      setStep(3);
    } catch (err) {
      console.error('[processFile] Error en matching:', err);
      setProcessingError(err?.message || 'Error al relacionar productos');
    }
  };

  // ─── PROCESS MANUAL ENTRY ─────────────────────────────────────────────────
  // Builds the same normalized object as processInvoice() and feeds the same flow.
  const processManualEntry = async () => {
    setShowManualEntry(false);
    setStep(2);
    setProcessingError(null);
    setProcessingStep(0);
    setProcessingLog([]);
    addLog('Procesando entrada manual...');

    try {
      const normalizedResult = buildManualInvoiceResult(manualForm);
      addLog(`✓ Datos manuales normalizados — ${normalizedResult?.lineas?.length || 0} líneas`);

      setProcessingStep(3);
      const provInfo = await matchProvider(normalizedResult?.cabecera || {});
      setProveedorInfo(provInfo);

      let mappings = [];
      if (provInfo?.id) {
        const { data: mappingData } = await tiendaDb?.from('ocr_product_mappings')
          ?.select('*, producto:productos(id, nombre, codigo_interno, ean)')
          ?.eq('proveedor_id', provInfo?.id);
        mappings = mappingData || [];
        addLog(`${mappings?.length} relaciones aprendidas cargadas`);
      }

      setProcessingStep(4);
      const lineas = normalizedResult?.lineas || [];
      const allMatched = await Promise.all(lineas?.map(linea => matchProduct(linea, provInfo?.id, mappings)));
      addLog(`✓ ${allMatched?.filter(l => l?.estado === 'VERDE')?.length} verdes, ${allMatched?.filter(l => l?.estado === 'AMARILLO')?.length} amarillas, ${allMatched?.filter(l => l?.estado === 'ROJO')?.length} rojas`);

      setProcessingStep(5);
      const cabecera = normalizedResult?.cabecera || {};
      setReviewCabecera({
        proveedor_id: provInfo?.id || '',
        proveedor_nombre: provInfo?.nombre || cabecera?.proveedor || '',
        numero_factura: cabecera?.numero_factura || '',
        fecha_factura: normalizeInvoiceDate(cabecera?.fecha) ?? '',
        fecha_vencimiento: normalizeInvoiceDate(cabecera?.fecha_vencimiento) ?? '',
        forma_pago: cabecera?.forma_pago || '',
        base_imponible: cabecera?.base_imponible || 0,
        iva: cabecera?.iva || 0,
        total: cabecera?.total || 0,
        descuentos: cabecera?.descuentos || 0,
        portes: cabecera?.portes || 0,
        cif_proveedor: cabecera?.cif || '',
        observaciones: cabecera?.observaciones || '',
      });

      setReviewLines(allMatched);
      setOcrResult({ ...normalizedResult, storagePath: null, _isManual: true });
      setStep(3);
    } catch (err) {
      console.error('[processManualEntry] Error:', err);
      setProcessingError(err?.message || 'Error al procesar la entrada manual');
    }
  };

  // ─── APPROVE ALL GREEN ────────────────────────────────────────────────────
  const approveAllGreen = () => {
    setReviewLines(prev => prev?.map(l => l?.estado === 'VERDE' ? { ...l, aprobado: true } : l));
  };

  // ─── EXCLUDE LINE FROM STOCK ──────────────────────────────────────────────
  const excluirLinea = (key) => {
    setReviewLines(prev => prev?.map(l => l?._key === key ? { ...l, afecta_stock: false, estado: 'AMARILLO' } : l));
  };

  // ─── UPDATE REVIEW LINE ───────────────────────────────────────────────────
  const updateReviewLine = (key, field, value) => {
    setReviewLines(prev => prev?.map(l => {
      if (l?._key !== key) return l;
      const updated = { ...l, [field]: value, editado: true };
      // Recalculate pricing when relevant fields change
      if (['cantidad', 'precio_unitario', 'descuento', 'descuento_porcentaje', 'porcentaje_iva', 'margen']?.includes(field)) {
        const precioUnitario = parseFloat(field === 'precio_unitario' ? value : updated?.precio_unitario) || 0;
        const descuento = parseFloat(field === 'descuento' ? value : updated?.descuento) || 0;
        const descPct = parseFloat(field === 'descuento_porcentaje' ? value : updated?.descuento_porcentaje) || 0;
        const ivaPct = parseFloat(field === 'porcentaje_iva' ? value : updated?.porcentaje_iva) || 10;
        const margen = parseFloat(field === 'margen' ? value : updated?.margen) || margenDefault;
        const recargoEquivalenciaPct = parseFloat(updated?.producto_sugerido?.recargo_equivalencia_pct) || 0;
        const { costeNeto, costeConIva, costeConImpuestos } = calcularCosteLinea({
          precioUnitario,
          descuentoImporte: descuento,
          descuentoPorcentaje: descPct,
          ivaPct,
          recargoEquivalenciaPct,
        });
        // PVP and margin based on coste_con_impuestos (real cost)
        const { pvpConIva, pvpSinIva, margenReal } = calcularPvp(costeConImpuestos, margen, ivaPct);
        const pvpPsico = calcularPvpPsicologico(pvpConIva, reglaRedondeo);
        updated.coste_neto = costeNeto;
        updated.coste_con_iva = costeConIva;
        updated.coste_con_impuestos = costeConImpuestos;
        updated.pvp_teorico = pvpConIva;
        updated.pvp_recomendado = pvpPsico;
        updated.pvp_sin_iva = pvpSinIva;
        updated.margen_real = margenReal;
        // Recalculate subtotal
        const qty = parseFloat(field === 'cantidad' ? value : updated?.cantidad) || 0;
        updated.subtotal = qty * precioUnitario;
      }
      // Auto-recalculate factor_conversion when cantidad_compra or cantidad_inventario changes
      if (['cantidad_compra', 'cantidad_inventario']?.includes(field)) {
        const cCompra = parseFloat(field === 'cantidad_compra' ? value : updated?.cantidad_compra) || 0;
        const cInv = field === 'cantidad_inventario'
          ? (value != null && value !== '' ? parseFloat(value) : null)
          : updated?.cantidad_inventario;
        if (cCompra > 0 && cInv != null && cInv > 0) {
          const newFactor = cInv / cCompra;
          // Check against product's saved factor
          const savedFactor = updated?.producto_sugerido?.factor_conversion != null
            ? parseFloat(updated?.producto_sugerido?.factor_conversion)
            : null;
          if (savedFactor != null && Math.abs(newFactor - savedFactor) > 0.01) {
            updated.warning_conversion = `Factor calculado (${newFactor?.toFixed(2)}) difiere del guardado en el producto (${savedFactor}). Revisa antes de confirmar.`;
          } else {
            updated.warning_conversion = null;
          }
          updated.factor_conversion = parseFloat(newFactor?.toFixed(4));
        } else if (cInv == null || cInv === 0) {
          updated.factor_conversion = null;
        }
      }
      return updated;
    }));
  };

  const assignProduct = (key, producto) => {
    setReviewLines(prev => prev?.map(l => {
      if (l?._key !== key) return l;
      const margen = getMargenAplicable(producto, margenDefault);
      const precioUnitario = parseFloat(l?.precio_unitario) || 0;
      const descuento = parseFloat(l?.descuento) || 0;
      const descPct = parseFloat(l?.descuento_porcentaje) || 0;
      const ivaPct = parseFloat(l?.porcentaje_iva) || 10;
      const recargoEquivalenciaPct = parseFloat(producto?.recargo_equivalencia_pct) || 0;
      const { costeNeto, costeConIva, costeConImpuestos } = calcularCosteLinea({
        precioUnitario,
        descuentoImporte: descuento,
        descuentoPorcentaje: descPct,
        ivaPct,
        recargoEquivalenciaPct,
      });
      const { pvpConIva, pvpSinIva, margenReal } = calcularPvp(costeConImpuestos, margen, ivaPct);
      const pvpPsico = calcularPvpPsicologico(pvpConIva, reglaRedondeo);
      return {
        ...l,
        producto_sugerido: producto,
        producto_id: producto?.id,
        estado: 'VERDE',
        confianza: 100,
        editado: true,
        isProvisional: false,
        margen,
        coste_neto: costeNeto,
        coste_con_iva: costeConIva,
        coste_con_impuestos: costeConImpuestos,
        pvp_teorico: pvpConIva,
        pvp_recomendado: pvpPsico,
        pvp_sin_iva: pvpSinIva,
        margen_real: margenReal,
      };
    }));
    setSelectorLine(null);
    setSelectorSearch('');
  };

  // ─── OPEN NEW PRODUCT FORM ────────────────────────────────────────────────
  const openNewProductForm = (lineaKey) => {
    const linea = reviewLines?.find(l => l?._key === lineaKey);
    if (!linea) return;
    const { categoria: catSugerida } = suggestCategory(linea?.descripcion_original);
    setNewProductForm({
      _lineaKey: lineaKey,
      nombre: linea?.descripcion_original || '',
      descripcion_proveedor: linea?.descripcion_original || '',
      referencia_proveedor: linea?.referencia_proveedor || '',
      ean: linea?.ean || '',
      precio_compra: linea?.precio_unitario || '',
      iva_compra: linea?.porcentaje_iva || 10,
      tipo_iva: linea?.porcentaje_iva || 10,
      unidad_compra: linea?.unidad_compra || linea?.unidad || 'UNIDAD',
      unidad_inventario: linea?.unidad_inventario || 'ud',
      factor_conversion: linea?.factor_conversion != null ? linea?.factor_conversion : '',
      activo: true,
      margen: (margenDefault * 100)?.toFixed(0),
      categoria_sugerida: catSugerida,
      familia_id: '',
    });
    // Cargar familias si no están cargadas
    if (familiasProducto?.length === 0) {
      tiendaDb?.from('familias_producto')?.select('id, nombre, epelsa_family_code, epelsa_sync_enabled')?.eq('activo', true)?.order('nombre')?.then(({ data }) => setFamiliasProducto(data || []));
    }
    setNewProductLine(lineaKey);
    setSelectorLine(null);
  };

  // ─── SAVE NEW PRODUCT FROM FORM ───────────────────────────────────────────
  const saveNewProduct = async () => {
    const lineaKey = newProductForm?._lineaKey;
    if (!lineaKey) return;
    if (!newProductForm?.familia_id) {
      alert('La familia es obligatoria. Selecciona una familia antes de crear el producto.');
      return;
    }
    try {
      // Generar codigo_interno desde la función SQL — única autoridad
      console.log('[SAVE NEW PRODUCT] Generando codigo_interno...');
      const codigoInterno = await generarCodigoInterno();
      console.log('[SAVE NEW PRODUCT] codigo_interno generado:', codigoInterno);

      if (!codigoInterno || typeof codigoInterno !== 'string' || !codigoInterno?.startsWith('LDD-')) {
        throw new Error(`No se pudo generar codigo_interno. Valor recibido: ${String(codigoInterno)}. Alta cancelada.`);
      }

      const productData = {
        nombre: newProductForm?.nombre || 'PRODUCTO DESCONOCIDO',
        descripcion: newProductForm?.descripcion_proveedor || null,
        referencia_proveedor: newProductForm?.referencia_proveedor || null,
        ean: newProductForm?.ean || null,
        precio_compra: parseFloat(newProductForm?.precio_compra) || null,
        iva_compra: parseFloat(newProductForm?.iva_compra) || 10,
        tipo_iva: parseFloat(newProductForm?.tipo_iva) || 10,
        unidad_compra: newProductForm?.unidad_compra || 'UNIDAD',
        unidad_inventario: newProductForm?.unidad_inventario || 'ud',
        factor_conversion: newProductForm?.factor_conversion !== '' && newProductForm?.factor_conversion != null
          ? parseFloat(newProductForm?.factor_conversion)
          : null,
        activo: true,
        proveedor_principal_id: reviewCabecera?.proveedor_id || null,
        margen_objetivo: parseFloat(newProductForm?.margen) || 40,
        // familia_id y codigo_interno SIEMPRE al final del objeto para que nunca sean sobrescritos
        familia_id: newProductForm?.familia_id,
        codigo_interno: codigoInterno,
        epelsa_sync_status: 'PENDIENTE_ALTA',
      };

      // Remove null ean to avoid unique constraint issues
      if (!productData?.ean) delete productData?.ean;

      console.log('[PAYLOAD FINAL REAL]', productData);

      const { data: newProd, error } = await tiendaDb?.from('productos')?.insert(productData)?.select()?.single();
      if (error) throw error;

      // Assign to line
      const margen = (parseFloat(newProductForm?.margen) || 40) / 100;
      const precioUnitario = parseFloat(newProductForm?.precio_compra) || 0;
      const ivaPct = parseFloat(newProductForm?.tipo_iva) || 10;
      const { costeNeto, costeConIva, costeConImpuestos } = calcularCosteLinea({
        precioUnitario,
        ivaPct,
      });
      const { pvpConIva, pvpSinIva, margenReal } = calcularPvp(costeConImpuestos, margen, ivaPct);
      const pvpPsico = calcularPvpPsicologico(pvpConIva, reglaRedondeo);

      setReviewLines(prev => prev?.map(l => {
        if (l?._key !== lineaKey) return l;
        return {
          ...l,
          producto_sugerido: newProd,
          producto_id: newProd?.id,
          estado: 'VERDE',
          confianza: 100,
          isProvisional: true,
          editado: true,
          margen,
          coste_neto: costeNeto,
          coste_con_iva: costeConIva,
          coste_con_impuestos: costeConImpuestos,
          pvp_teorico: pvpConIva,
          pvp_recomendado: pvpPsico,
          pvp_sin_iva: pvpSinIva,
          margen_real: margenReal,
        };
      }));
      setNewProductLine(null);
      setNewProductForm({});
    } catch (e) {
      alert(`Error al crear producto: ${e?.message}`);
    }
  };

  // ─── CREATE ALL MISSING PRODUCTS — DESHABILITADO ──────────────────────────
  // La creación masiva automática está deshabilitada para evitar duplicados.
  // Cada línea sin producto debe vincularse manualmente por el usuario.
  const crearTodosProductosFaltantes = () => {
    alert('La creación automática de productos está deshabilitada.\n\nPor cada línea sin vincular, usa "Buscar" para seleccionar un producto existente o "Crear nuevo" para crear uno individualmente.');
  };

  // ─── CHECK DUPLICATES ─────────────────────────────────────────────────────
  const checkDuplicates = async () => {
    if (!reviewCabecera?.proveedor_id || !reviewCabecera?.numero_factura) return false;
    const { data } = await tiendaDb?.from('facturas_compra')
      ?.select('id')
      ?.eq('proveedor_id', reviewCabecera?.proveedor_id)
      ?.eq('numero_factura', reviewCabecera?.numero_factura)
      ?.limit(1);
    return (data?.length || 0) > 0;
  };

  // ─── SAVE FACTURA (FULL FLOW) ─────────────────────────────────────────────
  // This is the "Confirmar factura y entrada de mercancía" action.
  // Phase 1: saves header + lines
  // Phase 2: calls confirmarFacturaYEntradaMercancia (transactional, idempotent)
  const saveFactura = async () => {
    setSaving(true);
    setSaveError(null);

    try {
      const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
      const session = sessionData?.session;
      if (sessionError || !session) {
        setSaveError('Tu sesión ha caducado. Inicia sesión de nuevo.');
        setSaving(false);
        return;
      }

      const isDuplicate = await checkDuplicates();
      if (isDuplicate) {
        setSaveError(`Ya existe una factura del mismo proveedor con el número "${reviewCabecera?.numero_factura}".`);
        setSaving(false);
        return;
      }

      const fechaFactura = normalizeInvoiceDate(reviewCabecera?.fecha_factura);
      const fechaVencimiento = normalizeInvoiceDate(reviewCabecera?.fecha_vencimiento);
      if (!fechaFactura) {
        setSaveError('La fecha de la factura no es válida. Revísala antes de guardar.');
        setSaving(false);
        return;
      }

      // ── STEP 1: Get default almacen ──────────────────────────────────────
      let defaultAlmacenId = null;
      try {
        const { data: almacenes } = await tiendaDb?.from('almacenes')?.select('id')?.eq('activo', true)?.limit(1);
        defaultAlmacenId = almacenes?.[0]?.id || null;
      } catch {}

      // ── STEP 2: Save invoice header ──────────────────────────────────────
      const cabecera = {
        proveedor_id: reviewCabecera?.proveedor_id || null,
        numero_factura: reviewCabecera?.numero_factura || `OCR-${Date.now()}`,
        fecha_factura: fechaFactura,
        fecha_vencimiento: fechaVencimiento || null,
        forma_pago: reviewCabecera?.forma_pago || null,
        base_imponible: parseFloat(reviewCabecera?.base_imponible) || 0,
        iva: parseFloat(reviewCabecera?.iva) || 0,
        total: parseFloat(reviewCabecera?.total) || 0,
        descuentos: parseFloat(reviewCabecera?.descuentos) || 0,
        portes: parseFloat(reviewCabecera?.portes) || 0,
        cif_proveedor: reviewCabecera?.cif_proveedor || null,
        observaciones: reviewCabecera?.observaciones || null,
        estado: 'PENDIENTE',
        estado_recepcion: 'PENDIENTE',
        estado_pago: 'PENDIENTE',
        importe_pagado: 0,
        pendiente_pago: parseFloat(reviewCabecera?.total) || 0,
        ocr_procesado: true,
        ocr_json: ocrResult ? { cabecera: ocrResult?.cabecera, lineas: ocrResult?.lineas } : null,
        ocr_confianza: ocrResult?.confianza_global != null
          ? (ocrResult?.confianza_global > 1 ? ocrResult?.confianza_global : ocrResult?.confianza_global * 100)
          : null,
        ocr_fecha_procesamiento: new Date()?.toISOString(),
        ocr_usuario: 'ERP',
        documento_url: ocrResult?.storagePath || null,
      };

      const { data: facturaCreada, error: facturaError } = await tiendaDb
        ?.from('facturas_compra')?.insert(cabecera)?.select()?.single();
      if (facturaError) throw facturaError;

      // ── STEP 3: Save lines ───────────────────────────────────────────────
      const lineasPayload = reviewLines
        ?.filter(l => l?.descripcion_original)
        ?.map(l => {
          const precioUnitario = parseFloat(l?.precio_unitario) || 0;
          const descuento = parseFloat(l?.descuento) || 0;
          const descPct = parseFloat(l?.descuento_porcentaje) || 0;
          const ivaPct = parseFloat(l?.porcentaje_iva) || 10;
          const recargoEquivalenciaPct = parseFloat(l?.producto_sugerido?.recargo_equivalencia_pct) || 0;
          const { costeNeto, costeConIva, costeConImpuestos } = calcularCosteLinea({
            precioUnitario,
            descuentoImporte: descuento,
            descuentoPorcentaje: descPct,
            ivaPct,
            recargoEquivalenciaPct,
          });
          const cantidadInv = l?.cantidad_inventario != null
            ? parseFloat(l?.cantidad_inventario)
            : (parseFloat(l?.cantidad) || 0);
          return {
            factura_id: facturaCreada?.id,
            producto_id: l?.producto_id || null,
            descripcion: l?.descripcion_original || null,
            descripcion_original: l?.descripcion_original || null,
            referencia_proveedor: l?.referencia_proveedor || null,
            ean_proveedor: l?.ean || null,
            // Stock quantity: use cantidad_inventario when available, fallback to cantidad
            cantidad: cantidadInv,
            unidad: l?.unidad_inventario || l?.unidad || null,
            // Logistic split fields
            cantidad_compra: l?.cantidad_compra != null ? parseFloat(l?.cantidad_compra) : null,
            unidad_compra: l?.unidad_compra || null,
            cantidad_inventario: l?.cantidad_inventario != null ? parseFloat(l?.cantidad_inventario) : null,
            unidad_inventario: l?.unidad_inventario || null,
            factor_conversion: l?.factor_conversion != null ? parseFloat(l?.factor_conversion) : null,
            precio_unitario: precioUnitario,
            precio_bruto: parseFloat(l?.precio_bruto || l?.precio_unitario) || 0,
            descuento: descuento,
            descuento_porcentaje: descPct,
            // All three cost fields stored separately
            precio_compra_neto: costeNeto,
            precio_compra_con_iva: costeConIva,
            coste_con_impuestos: costeConImpuestos,
            coste_total_linea: costeConImpuestos * cantidadInv,
            porcentaje_iva: ivaPct,
            subtotal: parseFloat(l?.subtotal) || 0,
            numero_lote: l?.lote || null,
            fecha_caducidad: l?.caducidad || null,
            ocr_confianza: l?.confianza || null,
            ocr_estado: l?.estado || 'MANUAL',
            afecta_stock: l?.afecta_stock !== false,
            pvp_calculado: parseFloat(l?.pvp_recomendado) || null,
            margen_aplicado: l?.margen ? l?.margen * 100 : null,
          };
        });

      let lineasCreadas = [];
      if (lineasPayload?.length > 0) {
        const { data: lineasData, error: lineasError } = await tiendaDb?.from('lineas_factura_compra')?.insert(lineasPayload)?.select();
        if (lineasError) throw lineasError;
        lineasCreadas = lineasData || [];
      }

      // ── STEP 4: Update product PURCHASE prices only — NO campos maestros Epelsa ──
      // PROTECCIÓN EPELSA: NO modificar plu, ean, nombre, familia_id, pvp_venta.
      // pvp_venta es el PVP maestro procedente de Epelsa y NO debe sobrescribirse.
      // El PVP calculado por el ERP se guarda en pvp_teorico (campo auxiliar).
      const historicoPreciosPayload = [];
      const productosActualizados = new Set();

      for (const l of reviewLines) {
        if (!l?.producto_id) continue;
        const precioUnitario = parseFloat(l?.precio_unitario) || 0;
        const descuento = parseFloat(l?.descuento) || 0;
        const descPct = parseFloat(l?.descuento_porcentaje) || 0;
        const ivaPct = parseFloat(l?.porcentaje_iva) || 10;
        const recargoEquivalenciaPct = parseFloat(l?.producto_sugerido?.recargo_equivalencia_pct) || 0;
        const { costeNeto, costeConIva, costeConImpuestos } = calcularCosteLinea({
          precioUnitario,
          descuentoImporte: descuento,
          descuentoPorcentaje: descPct,
          ivaPct,
          recargoEquivalenciaPct,
        });

        // Price history
        historicoPreciosPayload?.push({
          producto_id: l?.producto_id,
          proveedor_id: reviewCabecera?.proveedor_id || null,
          factura_compra_id: facturaCreada?.id,
          fecha: fechaFactura,
          precio_bruto: parseFloat(l?.precio_bruto || l?.precio_unitario) || 0,
          descuento_porcentaje: descPct,
          precio_neto: costeNeto,
          iva_porcentaje: ivaPct,
          coste_total: costeConImpuestos * (parseFloat(l?.cantidad) || 0),
        });

        // Actualizar SOLO campos de compra — NUNCA campos maestros de Epelsa
        // NO tocar: plu, ean, nombre, familia_id, pvp_venta
        if (!productosActualizados?.has(l?.producto_id)) {
          productosActualizados?.add(l?.producto_id);
          try {
            await tiendaDb?.from('productos')?.update({
              precio_compra: costeNeto,
              precio_compra_con_iva: costeConIva,
              coste_con_impuestos: costeConImpuestos,
              iva_compra: ivaPct,
              // pvp_teorico es campo auxiliar — NO es el PVP maestro de Epelsa
              pvp_teorico: parseFloat(l?.pvp_teorico) || null,
              // pvp_venta NO se modifica — es el PVP maestro procedente de Epelsa
              updated_at: new Date()?.toISOString(),
            })?.eq('id', l?.producto_id);
          } catch {}
        }
      }

      if (historicoPreciosPayload?.length > 0) {
        try {
          await tiendaDb?.from('historico_precios_compra')?.insert(historicoPreciosPayload);
        } catch {}
      }

      // ── STEP 5: ELIMINADO — Circuito duplicado de inventario/movimientos ──
      // El inventario y los movimientos de stock se gestionan ÚNICAMENTE mediante
      // confirmarFacturaYEntradaMercancia() en el STEP 7.
      // Eliminar este bloque evita que una misma factura incremente el stock dos veces.

      // ── STEP 6: Save OCR mappings ────────────────────────────────────────
      if (reviewCabecera?.proveedor_id) {
        const mappingsToSave = reviewLines
          ?.filter(l => l?.producto_id && l?.descripcion_original && !l?.isProvisional)
          ?.map(l => ({
            proveedor_id: reviewCabecera?.proveedor_id,
            descripcion_proveedor: l?.descripcion_original,
            referencia_proveedor: l?.referencia_proveedor || null,
            ean_proveedor: l?.ean || null,
            producto_id: l?.producto_id,
            confianza: l?.editado ? 100 : (l?.confianza || 85),
            veces_confirmado: 1,
            ultima_confirmacion: new Date()?.toISOString(),
          }));
        if (mappingsToSave?.length > 0) {
          try {
            await tiendaDb?.from('ocr_product_mappings')?.upsert(mappingsToSave, {
              onConflict: 'proveedor_id,descripcion_proveedor',
              ignoreDuplicates: false,
            });
          } catch {}
        }
      }

      // ── STEP 7: Confirmar factura y entrada de mercancía (circuito único) ──
      // ÚNICO circuito que gestiona inventario y movimientos de stock.
      // Crea lotes, movimientos ENTRADA, actualiza inventario_actual con coste_medio,
      // actualiza costes de producto, guarda historico_precios_compra y registra pago pendiente.
      let confirmResult = { ok: false, lineasStock: 0, lotesCreados: 0, pvpActualizados: 0, errores: [] };
      try {
        confirmResult = await confirmarFacturaYEntradaMercancia(facturaCreada?.id, {
          usuario: session?.user?.email || 'ERP',
        });
      } catch (confirmErr) {
        // Non-fatal: invoice is saved, stock entry failed
        console.warn('[saveFactura] confirmar error (non-fatal):', confirmErr?.message);
        confirmResult.errores = [confirmErr?.message];
      }

      setSavedFactura(facturaCreada);
      setSaveResult({
        productosCreados: reviewLines?.filter(l => l?.isProvisional && l?.producto_id)?.length,
        productosVinculados: reviewLines?.filter(l => !l?.isProvisional && l?.producto_id)?.length,
        movimientosCreados: confirmResult?.lineasStock || 0,
        stockActualizado: confirmResult?.lineasStock || 0,
        lotesCreados: confirmResult?.lotesCreados || 0,
        lineasPendientes: reviewLines?.filter(l => !l?.producto_id)?.length,
        preciosActualizados: productosActualizados?.size,
        pvpCalculados: confirmResult?.pvpActualizados || reviewLines?.filter(l => l?.pvp_recomendado > 0)?.length,
        total: parseFloat(reviewCabecera?.total) || 0,
        fechaVencimiento: fechaVencimiento,
        erroresConfirmacion: confirmResult?.errores || [],
      });
      setStep(4);
      onSaved?.();

    } catch (err) {
      console.error('[saveFactura] Error:', err);
      const msg = err?.message || 'Error al guardar la factura';
      if (msg?.includes('row-level security') || msg?.includes('violates row-level security policy')) {
        setSaveError('No tienes permisos para guardar esta factura. Comprueba que tu sesión está activa.');
      } else if (msg?.includes('date/time field value out of range') || msg?.includes('invalid input syntax for type date')) {
        setSaveError('La fecha no tiene un formato válido. Revisa los campos Fecha y Fecha de vencimiento.');
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // ─── STATS ────────────────────────────────────────────────────────────────
  const statsVerdes = reviewLines?.filter(l => l?.estado === 'VERDE')?.length;
  const statsAmarillos = reviewLines?.filter(l => l?.estado === 'AMARILLO')?.length;
  const statsRojos = reviewLines?.filter(l => l?.estado === 'ROJO')?.length;
  const statsProvisionales = reviewLines?.filter(l => l?.isProvisional)?.length;

  const inputCls = "w-full px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none";
  const inputSt = { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={step !== 2 ? onClose : undefined} />
      {/* Modal */}
      <div className="fixed z-50 flex flex-col" style={{ top: '12px', bottom: '12px', right: '12px', left: '12px' }}>
        <div className="flex flex-col w-full h-full rounded-2xl overflow-hidden lg:ml-[280px]"
          style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.10)', width: 'auto' }}
          onClick={e => e?.stopPropagation()}>

          {/* ── COMPACT HEADER ──────────────────────────────────────────── */}
          <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2.5 flex-wrap"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}30` }}>
                <Icon name="ScanLine" size={14} style={{ color: BLUE }} />
              </div>
              <span className="font-oranienbaum font-bold text-white text-sm">Importar Factura</span>
            </div>

            {step === 3 && reviewCabecera && (
              <>
                <div className="h-4 w-px bg-white/10 flex-shrink-0 hidden sm:block" />
                <span className="font-oranienbaum text-white/60 text-xs flex-shrink-0 hidden sm:block">
                  {proveedorInfo?.nombre || reviewCabecera?.proveedor_nombre || '—'}
                </span>
                <div className="h-4 w-px bg-white/10 flex-shrink-0 hidden md:block" />
                <span className="font-oranienbaum text-white/50 text-xs flex-shrink-0 hidden md:block">
                  Nº {reviewCabecera?.numero_factura || '—'}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: `${ACCENT}12`, color: ACCENT }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: ACCENT }} />{statsVerdes}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: `${AMBER}12`, color: AMBER }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: AMBER }} />{statsAmarillos}
                  </span>
                  {statsRojos > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: `${RED}12`, color: RED }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: RED }} />{statsRojos}
                    </span>
                  )}
                </div>
                <div className="h-4 w-px bg-white/10 flex-shrink-0 hidden sm:block" />
                <button onClick={approveAllGreen}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-oranienbaum font-semibold flex-shrink-0"
                  style={{ background: `${ACCENT}10`, color: ACCENT, border: `1px solid ${ACCENT}25` }}>
                  <Icon name="CheckCheck" size={11} /><span className="hidden sm:inline">Aceptar verdes</span>
                </button>
                {/* Botón "Crear faltantes" eliminado — creación masiva automática deshabilitada */}
                <button onClick={recalcularTodosPvp}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-oranienbaum font-semibold flex-shrink-0"
                  style={{ background: `${BLUE}10`, color: BLUE, border: `1px solid ${BLUE}25` }}>
                  <Icon name="RefreshCw" size={11} /><span className="hidden sm:inline">Recalcular PVP</span>
                </button>
                <button onClick={() => setShowPricingSettings(s => !s)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-oranienbaum font-semibold flex-shrink-0"
                  style={{ background: `${PURPLE}10`, color: PURPLE, border: `1px solid ${PURPLE}25` }}>
                  <Icon name="Settings" size={11} /><span className="hidden sm:inline">Márgenes</span>
                </button>
              </>
            )}

            <div className="flex-1" />
            <div className="flex-shrink-0"><StepIndicator currentStep={step} /></div>
            <button onClick={onClose} className="flex-shrink-0 p-1.5 rounded-lg text-white/40 hover:text-white/70 transition-colors">
              <Icon name="X" size={16} />
            </button>
          </div>

          {/* ── PRICING SETTINGS PANEL ───────────────────────────────────── */}
          {showPricingSettings && step === 3 && (
            <div className="flex-shrink-0 px-4 py-3 flex flex-wrap gap-4 items-center"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: `${PURPLE}05` }}>
              <div className="flex items-center gap-2">
                <span className="font-oranienbaum text-white/50 text-xs">Margen por defecto:</span>
                <input type="number" min="0" max="99" step="1"
                  value={(margenDefault * 100)?.toFixed(0)}
                  onChange={e => setMargenDefault(parseFloat(e?.target?.value) / 100 || MARGEN_DEFAULT)}
                  className="w-16 px-2 py-1 rounded-lg text-xs font-oranienbaum text-white/80 outline-none text-right"
                  style={inputSt} />
                <span className="font-oranienbaum text-white/40 text-xs">%</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-oranienbaum text-white/50 text-xs">Redondeo:</span>
                <select value={reglaRedondeo} onChange={e => setReglaRedondeo(e?.target?.value)}
                  className="px-2 py-1 rounded-lg text-xs font-oranienbaum outline-none"
                  style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.80)' }}>
                  <option value="terminar_99">Terminar en ,99</option>
                  <option value="terminar_95">Terminar en ,95</option>
                  <option value="terminar_90">Terminar en ,90</option>
                  <option value="multiplos_005">Múltiplos de 0,05</option>
                  <option value="multiplos_010">Múltiplos de 0,10</option>
                  <option value="a_la_baja">Siempre a la baja</option>
                  <option value="sin_redondeo">Sin redondeo</option>
                </select>
              </div>
              <button onClick={recalcularTodosPvp}
                className="flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-oranienbaum"
                style={{ background: `${BLUE}15`, color: BLUE, border: `1px solid ${BLUE}30` }}>
                <Icon name="RefreshCw" size={12} />Aplicar a todas
              </button>
            </div>
          )}

          {/* ── SCROLLABLE BODY ──────────────────────────────────────────── */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">

            {/* ── STEP 1: UPLOAD ─────────────────────────────────────────── */}
            {step === 1 && (
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <h3 className="font-oranienbaum text-white/70 text-sm font-semibold text-center">Sube la factura del proveedor</h3>
                {!file ? (
                  <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
                    className="rounded-2xl p-10 flex flex-col items-center gap-4 cursor-pointer transition-all"
                    style={{ border: `2px dashed ${isDragging ? BLUE : 'rgba(255,255,255,0.15)'}`, background: isDragging ? `${BLUE}08` : 'rgba(255,255,255,0.02)' }}
                    onClick={() => fileInputRef?.current?.click()}>
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: `${BLUE}10`, border: `1px solid ${BLUE}20` }}>
                      <Icon name="Upload" size={28} style={{ color: BLUE }} />
                    </div>
                    <div className="text-center">
                      <p className="font-oranienbaum text-white/70 text-sm font-semibold">Arrastra aquí o haz clic para seleccionar</p>
                      <p className="font-oranienbaum text-white/35 text-xs mt-1">JPG · PNG · WEBP · PDF</p>
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                      onChange={e => handleFile(e?.target?.files?.[0])} />
                  </div>
                ) : (
                  <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.10)' }}>
                    {preview === 'pdf' ? (
                      <div className="p-8 flex flex-col items-center gap-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
                        <Icon name="FileText" size={40} style={{ color: BLUE }} />
                        <p className="font-oranienbaum text-white/70 text-sm">{file?.name}</p>
                      </div>
                    ) : (
                      <img src={preview} alt="Vista previa de la factura" className="w-full max-h-64 object-contain" style={{ background: 'rgba(255,255,255,0.03)' }} />
                    )}
                    <div className="px-4 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                      <span className="font-oranienbaum text-white/50 text-xs">{file?.name}</span>
                      <button onClick={() => { setFile(null); setPreview(null); }} className="text-xs font-oranienbaum text-red-400 hover:text-red-300">Cambiar</button>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <button onClick={() => cameraInputRef?.current?.click()}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-oranienbaum"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
                    <Icon name="Camera" size={16} />Usar cámara
                  </button>
                  <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={e => handleFile(e?.target?.files?.[0])} />
                  <button onClick={() => fileInputRef?.current?.click()}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-oranienbaum"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
                    <Icon name="FolderOpen" size={16} />Seleccionar archivo
                  </button>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-oranienbaum"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.50)' }}>
                    Cancelar
                  </button>
                  <button onClick={() => setShowManualEntry(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-oranienbaum"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.60)' }}>
                    <Icon name="PenLine" size={15} />Introducir manualmente
                  </button>
                  <button onClick={processFile} disabled={!file}
                    className="flex items-center gap-2 px-6 py-2 rounded-xl text-sm font-oranienbaum font-bold disabled:opacity-40"
                    style={{ background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }}>
                    <Icon name="Zap" size={15} />Procesar con OpenAI
                  </button>
                </div>
              </div>
            )}

            {/* ── STEP 2: PROCESSING ─────────────────────────────────────── */}
            {step === 2 && (
              <div className="flex-1 overflow-y-auto p-5">
                <div className="flex flex-col items-center gap-6 py-4">
                  {processingError ? (
                    <div className="w-full space-y-4">
                      <div className="p-5 rounded-xl flex flex-col items-center gap-3 text-center" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
                        <Icon name="AlertCircle" size={28} className="text-red-400" />
                        <p className="font-oranienbaum text-red-400 text-base font-bold">No se ha podido procesar el documento</p>
                        <p className="font-oranienbaum text-red-300/70 text-xs max-w-sm">{processingError}</p>
                      </div>
                      <p className="font-oranienbaum text-white/40 text-xs text-center">
                        No se ha guardado ninguna factura ni se ha modificado el inventario.
                      </p>
                      <div className="flex justify-center gap-3 flex-wrap">
                        <button onClick={() => { setProcessingError(null); setStep(1); }}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-oranienbaum"
                          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.60)' }}>
                          <Icon name="RotateCcw" size={14} />Reintentar
                        </button>
                        <button onClick={() => { setProcessingError(null); setStep(1); setShowManualEntry(true); }}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-oranienbaum font-semibold"
                          style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}30`, color: BLUE }}>
                          <Icon name="PenLine" size={14} />Introducir manualmente
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="relative w-20 h-20">
                        <div className="w-20 h-20 rounded-full border-4 border-white/10 border-t-[#60A5FA] animate-spin" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Icon name={PROCESSING_STEPS?.[processingStep]?.icon || 'Loader'} size={24} style={{ color: BLUE }} />
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="font-oranienbaum text-white font-semibold text-base">{PROCESSING_STEPS?.[processingStep]?.label || 'Procesando...'}</p>
                        <p className="font-oranienbaum text-white/35 text-xs mt-1">Procesando con OpenAI · Sin bloqueos</p>
                      </div>
                      <div className="w-full max-w-xs space-y-2">
                        {PROCESSING_STEPS?.map((s, i) => (
                          <div key={s?.id} className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                              style={i < processingStep ? { background: `${ACCENT}20`, border: `1px solid ${ACCENT}40` }
                                : i === processingStep ? { background: `${BLUE}20`, border: `1px solid ${BLUE}40` }
                                : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)' }}>
                              {i < processingStep ? <Icon name="Check" size={10} style={{ color: ACCENT }} />
                                : i === processingStep ? <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: BLUE }} /> : null}
                            </div>
                            <span className="font-oranienbaum text-xs" style={{ color: i <= processingStep ? 'rgba(255,255,255,0.70)' : 'rgba(255,255,255,0.25)' }}>{s?.label}</span>
                          </div>
                        ))}
                      </div>
                      {processingLog?.length > 0 && (
                        <div className="w-full max-w-sm max-h-24 overflow-y-auto rounded-xl p-3 space-y-1"
                          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                          {processingLog?.map((log, i) => <p key={i} className="font-oranienbaum text-white/40 text-[10px]">{log}</p>)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 3: REVIEW ─────────────────────────────────────────── */}
            {step === 3 && (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                {/* Sub-header */}
                <div className="flex-shrink-0 px-4 pt-3 pb-2 space-y-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {proveedorInfo && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon name="Building2" size={13} style={{ color: proveedorInfo?.isProvisional ? AMBER : ACCENT, flexShrink: 0 }} />
                      <span className="font-oranienbaum text-white/75 text-xs font-semibold">{proveedorInfo?.nombre}</span>
                      {proveedorInfo?.isProvisional && <ProvisionalBadge type="proveedor" />}
                      {!proveedorInfo?.isProvisional && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase"
                          style={{ background: `${ACCENT}15`, color: ACCENT, border: `1px solid ${ACCENT}30` }}>
                          <Icon name="CheckCircle2" size={7} />EXISTENTE
                        </span>
                      )}
                      {reviewCabecera?.cif_proveedor && (
                        <span className="font-oranienbaum text-white/35 text-[10px]">CIF: {reviewCabecera?.cif_proveedor}</span>
                      )}
                      <select value={reviewCabecera?.proveedor_id || ''}
                        onChange={e => {
                          const sel = proveedores?.find(p => p?.id === e?.target?.value);
                          setReviewCabecera(prev => ({ ...prev, proveedor_id: e?.target?.value }));
                          if (sel) setProveedorInfo({ id: sel?.id, nombre: sel?.nombre, isNew: false, isProvisional: false });
                        }}
                        className="ml-auto px-2 py-1 rounded-lg text-[10px] font-oranienbaum text-white/60 outline-none"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
                        <option value="">— Cambiar —</option>
                        {proveedores?.map(p => <option key={p?.id} value={p?.id}>{p?.nombre}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <label className="block font-oranienbaum text-white/30 text-[9px] mb-0.5 uppercase tracking-wider">Nº Factura</label>
                      <input type="text" value={reviewCabecera?.numero_factura || ''}
                        onChange={e => setReviewCabecera(p => ({ ...p, numero_factura: e?.target?.value }))}
                        className={inputCls} style={inputSt} />
                    </div>
                    <div>
                      <label className="block font-oranienbaum text-white/30 text-[9px] mb-0.5 uppercase tracking-wider">Fecha</label>
                      <input type="date" value={reviewCabecera?.fecha_factura || ''}
                        onChange={e => setReviewCabecera(p => ({ ...p, fecha_factura: e?.target?.value }))}
                        className={inputCls} style={{ ...inputSt, colorScheme: 'dark' }} />
                    </div>
                    <div>
                      <label className="block font-oranienbaum text-white/30 text-[9px] mb-0.5 uppercase tracking-wider">Vencimiento</label>
                      <input type="date" value={reviewCabecera?.fecha_vencimiento || ''}
                        onChange={e => setReviewCabecera(p => ({ ...p, fecha_vencimiento: e?.target?.value }))}
                        className={inputCls} style={{ ...inputSt, colorScheme: 'dark' }} />
                    </div>
                    <div>
                      <label className="block font-oranienbaum text-white/30 text-[9px] mb-0.5 uppercase tracking-wider">Total factura</label>
                      <input type="number" value={reviewCabecera?.total || ''}
                        onChange={e => setReviewCabecera(p => ({ ...p, total: parseFloat(e?.target?.value) || 0 }))}
                        className={`${inputCls} text-right`} style={inputSt} />
                    </div>
                  </div>
                  {validationWarning && (
                    <div className="p-2 rounded-lg flex items-start gap-2" style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.20)' }}>
                      <Icon name="AlertTriangle" size={12} className="text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="font-oranienbaum text-amber-300/80 text-[10px]">{validationWarning}</p>
                    </div>
                  )}
                </div>

                {/* ── TABLE ─────────────────────────────────────────────── */}
                <div className="flex-1 min-h-0 overflow-auto">
                  <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '2%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '5%' }} />
                      <col style={{ width: '5%' }} />
                    </colgroup>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                      <tr style={{ background: '#161616', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        {['', 'Descripción', 'Producto ERP', 'C.Compra', 'Formato', 'C.Inv.', 'Ud.Inv.', 'P.Bruto', 'Dto%', 'Coste', 'IVA', 'PVP Rec.', 'Stock+', 'Acc.']?.map(col => (
                          <th key={col} className="px-1.5 py-2 text-left">
                            <span className="font-oranienbaum text-white/30 text-[9px] uppercase tracking-wider">{col}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reviewLines?.map((linea) => {
                        const cfg = CONFIDENCE_CONFIG?.[linea?.estado] || CONFIDENCE_CONFIG?.ROJO;
                        const stockActual = inventarioActual?.[linea?.producto_id] || 0;
                        // Stock entry uses cantidad_inventario when available, fallback to cantidad
                        const cantidadParaStock = linea?.cantidad_inventario != null
                          ? parseFloat(linea?.cantidad_inventario)
                          : (parseFloat(linea?.cantidad) || 0);
                        const stockResultante = stockActual + cantidadParaStock;
                        const excluida = linea?.afecta_stock === false;
                        const hasConversionWarning = !!linea?.warning_conversion;
                        return (
                          <tr key={linea?._key}
                            style={{
                              borderBottom: '1px solid rgba(255,255,255,0.04)',
                              background: excluida ? 'rgba(255,255,255,0.01)' : linea?.estado === 'ROJO' ? 'rgba(248,113,113,0.04)' : linea?.estado === 'AMARILLO' ? 'rgba(251,191,36,0.03)' : 'transparent',
                              opacity: excluida ? 0.5 : 1,
                            }}>
                            {/* Status */}
                            <td className="px-1.5 py-2 align-middle">
                              <Icon name={excluida ? 'MinusCircle' : cfg?.icon} size={12} style={{ color: excluida ? 'rgba(255,255,255,0.25)' : cfg?.color }} title={excluida ? 'Excluida' : cfg?.label} />
                            </td>
                            {/* Descripción */}
                            <td className="px-1.5 py-2 align-middle" style={{ overflow: 'hidden' }}>
                              <p className="font-oranienbaum text-white/70 text-xs" title={linea?.descripcion_original}
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: '1.35' }}>
                                {linea?.descripcion_original}
                              </p>
                              {linea?.referencia_proveedor && (
                                <p className="font-oranienbaum text-white/25 text-[9px] mt-0.5">Ref: {linea?.referencia_proveedor}</p>
                              )}
                              {hasConversionWarning && (
                                <p className="font-oranienbaum text-[8px] mt-0.5" style={{ color: AMBER }} title={linea?.warning_conversion}>
                                  ⚠ {linea?.warning_conversion?.slice(0, 40)}{linea?.warning_conversion?.length > 40 ? '…' : ''}
                                </p>
                              )}
                            </td>
                            {/* Producto ERP */}
                            <td className="px-1.5 py-2 align-middle" style={{ overflow: 'hidden' }}>
                              {linea?.producto_sugerido ? (
                                <div>
                                  <button onClick={() => setSelectorLine(linea?._key)}
                                    className="font-oranienbaum text-xs text-left hover:underline block w-full truncate"
                                    style={{ color: linea?.isProvisional ? AMBER : ACCENT }}
                                    title={linea?.producto_sugerido?.nombre}>
                                    {linea?.producto_sugerido?.nombre}
                                  </button>
                                  {linea?.isProvisional && <ProvisionalBadge type="producto" />}
                                  {linea?.pvp_recomendado > 0 && (
                                    <p className="font-oranienbaum text-[9px] mt-0.5" style={{ color: BLUE }}>
                                      PVP: {fmtEur(linea?.pvp_recomendado)}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-col gap-0.5">
                                  <button onClick={() => setSelectorLine(linea?._key)}
                                    className="px-1.5 py-0.5 rounded text-[9px] font-oranienbaum text-left"
                                    style={{ background: `${BLUE}15`, color: BLUE, border: `1px solid ${BLUE}30` }}>
                                    Buscar
                                  </button>
                                  <button onClick={() => openNewProductForm(linea?._key)}
                                    className="px-1.5 py-0.5 rounded text-[9px] font-oranienbaum text-left"
                                    style={{ background: `${ACCENT}15`, color: ACCENT, border: `1px solid ${ACCENT}30` }}>
                                    Crear nuevo
                                  </button>
                                  <button onClick={() => excluirLinea(linea?._key)}
                                    className="px-1.5 py-0.5 rounded text-[9px] font-oranienbaum text-left"
                                    style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.40)', border: '1px solid rgba(255,255,255,0.10)' }}>
                                    No stock
                                  </button>
                                </div>
                              )}
                            </td>
                            {/* Cant. Compra */}
                            <td className="px-1.5 py-2 align-middle">
                              <input type="number" value={linea?.cantidad_compra ?? linea?.cantidad ?? ''} min="0" step="0.001"
                                onChange={e => updateReviewLine(linea?._key, 'cantidad_compra', e?.target?.value)}
                                className="w-full px-1 py-1 rounded text-xs font-oranienbaum text-white/80 outline-none text-right"
                                style={{ ...inputSt, minWidth: 0, height: '28px' }}
                                title="Cantidad de compra (cajas, bultos, etc.)" />
                            </td>
                            {/* Formato (unidad_compra) */}
                            <td className="px-1.5 py-2 align-middle">
                              <input type="text" value={linea?.unidad_compra || linea?.unidad || ''}
                                onChange={e => updateReviewLine(linea?._key, 'unidad_compra', e?.target?.value)}
                                className="w-full px-1 py-1 rounded text-xs font-oranienbaum text-white/80 outline-none"
                                style={{ ...inputSt, minWidth: 0, height: '28px' }}
                                placeholder="caja"
                                title="Formato de compra (caja, bulto, pack, kg…)" />
                            </td>
                            {/* Cant. Inventario */}
                            <td className="px-1.5 py-2 align-middle">
                              <input type="number"
                                value={linea?.cantidad_inventario != null ? linea?.cantidad_inventario : ''}
                                min="0" step="0.001"
                                onChange={e => updateReviewLine(linea?._key, 'cantidad_inventario', e?.target?.value === '' ? null : e?.target?.value)}
                                className="w-full px-1 py-1 rounded text-xs font-oranienbaum outline-none text-right"
                                style={{ ...inputSt, minWidth: 0, height: '28px', color: linea?.cantidad_inventario != null ? '#34D399' : 'rgba(255,255,255,0.40)' }}
                                placeholder="—"
                                title="Unidades que entran al inventario" />
                            </td>
                            {/* Ud. Inventario */}
                            <td className="px-1.5 py-2 align-middle">
                              <input type="text" value={linea?.unidad_inventario || ''}
                                onChange={e => updateReviewLine(linea?._key, 'unidad_inventario', e?.target?.value)}
                                className="w-full px-1 py-1 rounded text-xs font-oranienbaum text-white/80 outline-none"
                                style={{ ...inputSt, minWidth: 0, height: '28px' }}
                                placeholder="ud"
                                title="Unidad de inventario (ud, kg, litro…)" />
                            </td>
                            {/* Precio bruto */}
                            <td className="px-1.5 py-2 align-middle">
                              <input type="number" value={linea?.precio_unitario} min="0" step="0.01"
                                onChange={e => updateReviewLine(linea?._key, 'precio_unitario', e?.target?.value)}
                                className="w-full px-1 py-1 rounded text-xs font-oranienbaum text-white/80 outline-none text-right"
                                style={{ ...inputSt, minWidth: 0, height: '28px' }} />
                            </td>
                            {/* Descuento % */}
                            <td className="px-1.5 py-2 align-middle">
                              <input type="number" value={linea?.descuento_porcentaje || 0} min="0" max="100" step="0.1"
                                onChange={e => updateReviewLine(linea?._key, 'descuento_porcentaje', e?.target?.value)}
                                className="w-full px-1 py-1 rounded text-xs font-oranienbaum text-white/80 outline-none text-right"
                                style={{ ...inputSt, minWidth: 0, height: '28px' }} />
                            </td>
                            {/* Coste con impuestos — COSTE REAL SOPORTADO */}
                            <td className="px-1.5 py-2 align-middle text-right">
                              <span className="font-oranienbaum text-xs font-semibold" style={{ color: ACCENT }}>
                                {fmtEur(linea?.coste_con_impuestos ?? linea?.coste_neto ?? parseFloat(linea?.precio_unitario) ?? 0)}
                              </span>
                              {linea?.coste_neto != null && linea?.coste_con_impuestos != null && linea?.coste_con_impuestos !== linea?.coste_neto && (
                                <p className="font-oranienbaum text-[8px]" style={{ color: 'rgba(255,255,255,0.30)' }}>
                                  neto: {fmtEur(linea?.coste_neto)}
                                </p>
                              )}
                            </td>
                            {/* IVA */}
                            <td className="px-1.5 py-2 align-middle">
                              <div className="relative flex items-center">
                                <input type="number" value={linea?.porcentaje_iva || 10} min="0" max="21"
                                  onChange={e => updateReviewLine(linea?._key, 'porcentaje_iva', e?.target?.value)}
                                  className="w-full pl-1 pr-3 py-1 rounded text-xs font-oranienbaum text-white/80 outline-none text-right"
                                  style={{ ...inputSt, minWidth: 0, height: '28px' }} />
                                <span className="absolute right-1 font-oranienbaum text-white/30 text-[9px] pointer-events-none">%</span>
                              </div>
                            </td>
                            {/* PVP recomendado */}
                            <td className="px-1.5 py-2 align-middle text-right">
                              <span className="font-oranienbaum text-xs font-semibold" style={{ color: linea?.pvp_recomendado > 0 ? BLUE : 'rgba(255,255,255,0.25)' }}>
                                {linea?.pvp_recomendado > 0 ? fmtEur(linea?.pvp_recomendado) : '—'}
                              </span>
                              {linea?.margen_real > 0 && (
                                <p className="font-oranienbaum text-[9px]" style={{ color: linea?.margen_real >= 0.30 ? ACCENT : AMBER }}>
                                  {(linea?.margen_real * 100)?.toFixed(0)}%
                                </p>
                              )}
                            </td>
                            {/* Stock resultante */}
                            <td className="px-1.5 py-2 align-middle text-right">
                              {linea?.producto_id && !excluida ? (
                                <div>
                                  <span className="font-oranienbaum text-xs" style={{ color: ACCENT }}>+{fmt(cantidadParaStock, 0)}</span>
                                  <p className="font-oranienbaum text-[9px] text-white/30">→{fmt(stockResultante, 0)}</p>
                                </div>
                              ) : (
                                <span className="font-oranienbaum text-white/20 text-xs">—</span>
                              )}
                            </td>
                            {/* Actions */}
                            <td className="px-1.5 py-2 align-middle">
                              <div className="flex flex-col gap-0.5">
                                {!excluida && (
                                  <button onClick={() => excluirLinea(linea?._key)} title="Excluir del stock"
                                    className="p-1 rounded text-white/20 hover:text-amber-400 transition-colors">
                                    <Icon name="EyeOff" size={10} />
                                  </button>
                                )}
                                {excluida && (
                                  <button onClick={() => setReviewLines(prev => prev?.map(l => l?._key === linea?._key ? { ...l, afecta_stock: true } : l))}
                                    title="Incluir en stock" className="p-1 rounded text-white/20 hover:text-green-400 transition-colors">
                                    <Icon name="Eye" size={10} />
                                  </button>
                                )}
                                <button onClick={() => setReviewLines(prev => prev?.filter(l => l?._key !== linea?._key))}
                                  className="p-1 rounded text-white/20 hover:text-red-400 transition-colors">
                                  <Icon name="Trash2" size={10} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ── STICKY FOOTER ────────────────────────────────────── */}
                <div className="flex-shrink-0 px-4 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: '#0F0F0F' }}>
                  {saveError && (
                    <div className="mb-2 p-2 rounded-lg flex items-start gap-2" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
                      <Icon name="AlertCircle" size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="font-oranienbaum text-red-400 text-xs">{saveError}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-4 flex-1 flex-wrap">
                      <div className="text-right">
                        <p className="font-oranienbaum text-white/30 text-[9px] uppercase tracking-wider">Base</p>
                        <p className="font-oranienbaum text-white/65 text-sm font-semibold">{fmtEur(parseFloat(reviewCabecera?.base_imponible) || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-oranienbaum text-white/30 text-[9px] uppercase tracking-wider">IVA</p>
                        <p className="font-oranienbaum text-white/65 text-sm font-semibold">{fmtEur(parseFloat(reviewCabecera?.iva) || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-oranienbaum text-white/30 text-[9px] uppercase tracking-wider">Total</p>
                        <p className="font-oranienbaum text-white font-bold text-base">{fmtEur(parseFloat(reviewCabecera?.total) || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-oranienbaum text-white/30 text-[9px] uppercase tracking-wider">Líneas con stock</p>
                        <p className="font-oranienbaum text-sm font-semibold" style={{ color: ACCENT }}>
                          {reviewLines?.filter(l => l?.producto_id && l?.afecta_stock !== false)?.length}/{reviewLines?.length}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => setStep(1)} disabled={saving} className="px-4 py-2 rounded-xl text-sm font-oranienbaum"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.50)' }}>
                        Volver
                      </button>
                      <button onClick={saveFactura} disabled={saving}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-oranienbaum font-bold"
                        style={{ background: saving ? `${ACCENT}10` : `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
                        {saving
                          ? <><div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-[#34D399] animate-spin" />Confirmando...</>
                          : <><Icon name="PackageCheck" size={14} />Confirmar factura y entrada de mercancía</>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 4: SUCCESS ────────────────────────────────────────── */}
            {step === 4 && (
              <div className="flex-1 overflow-y-auto p-5">
                <div className="flex flex-col items-center gap-5 py-6">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
                    <Icon name="CheckCircle2" size={36} style={{ color: ACCENT }} />
                  </div>
                  <div className="text-center">
                    <p className="font-oranienbaum text-white font-bold text-lg">Factura importada correctamente</p>
                    <p className="font-oranienbaum text-white/40 text-sm mt-1">
                      {savedFactura?.numero_factura} · {fmtEur(saveResult?.total)}
                    </p>
                  </div>

                  {/* Summary grid */}
                  {saveResult && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-lg">
                      {[
                        { label: 'Productos creados', value: saveResult?.productosCreados, color: AMBER },
                        { label: 'Productos vinculados', value: saveResult?.productosVinculados, color: ACCENT },
                        { label: 'Movimientos ENTRADA', value: saveResult?.movimientosCreados, color: BLUE },
                        { label: 'Lotes creados', value: saveResult?.lotesCreados, color: PURPLE },
                        { label: 'Precios actualizados', value: saveResult?.preciosActualizados, color: BLUE },
                        { label: 'PVP calculados', value: saveResult?.pvpCalculados, color: PURPLE },
                        { label: 'Líneas pendientes', value: saveResult?.lineasPendientes, color: saveResult?.lineasPendientes > 0 ? RED : ACCENT },
                        { label: 'Vencimiento', value: saveResult?.fechaVencimiento || 'Sin definir', color: GOLD },
                      ]?.map((item, i) => (
                        <div key={i} className="p-3 rounded-xl text-center" style={{ background: `${item?.color}08`, border: `1px solid ${item?.color}20` }}>
                          <p className="font-oranienbaum text-lg font-bold" style={{ color: item?.color }}>{item?.value}</p>
                          <p className="font-oranienbaum text-white/40 text-[10px]">{item?.label}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {saveResult?.erroresConfirmacion?.length > 0 && (
                    <div className="p-3 rounded-xl w-full max-w-lg" style={{ background: `${RED}08`, border: `1px solid ${RED}20` }}>
                      <p className="font-oranienbaum text-red-300/80 text-xs font-semibold mb-1">Advertencias en la confirmación:</p>
                      {saveResult?.erroresConfirmacion?.map((e, i) => (
                        <p key={i} className="font-oranienbaum text-red-300/60 text-xs">{e}</p>
                      ))}
                    </div>
                  )}

                  {saveResult?.lineasPendientes > 0 && (
                    <div className="p-3 rounded-xl w-full max-w-lg" style={{ background: `${AMBER}08`, border: `1px solid ${AMBER}20` }}>
                      <p className="font-oranienbaum text-amber-300/80 text-xs text-center">
                        ⚠ Hay {saveResult?.lineasPendientes} línea(s) sin producto asignado. Usa "Completar importación" en el detalle de la factura para vincularlas.
                      </p>
                    </div>
                  )}

                  <button onClick={onClose} className="px-6 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold"
                    style={{ background: `${BLUE}20`, color: BLUE, border: `1px solid ${BLUE}40` }}>
                    Cerrar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* ── PRODUCT SELECTOR MODAL ─────────────────────────────────────────── */}
      {selectorLine && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
          <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.12)' }}>
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 className="font-oranienbaum text-white font-semibold text-sm">Seleccionar producto</h3>
              <div className="flex items-center gap-2">
                <button onClick={() => openNewProductForm(selectorLine)}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-oranienbaum"
                  style={{ background: `${ACCENT}15`, color: ACCENT, border: `1px solid ${ACCENT}30` }}>
                  <Icon name="Plus" size={12} />Crear nuevo
                </button>
                <button onClick={() => { setSelectorLine(null); setSelectorSearch(''); }} className="p-1.5 rounded-lg text-white/40 hover:text-white/70">
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-3">
              {(() => {
                const currentLine = reviewLines?.find(l => l?._key === selectorLine);
                return currentLine ? (
                  <div className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <p className="font-oranienbaum text-white/50 text-[10px]">Descripción factura:</p>
                    <p className="font-oranienbaum text-white/80 text-xs">{currentLine?.descripcion_original}</p>
                  </div>
                ) : null;
              })()}
              <div className="relative">
                <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                <input type="text" placeholder="Buscar producto..." value={selectorSearch}
                  onChange={e => setSelectorSearch(e?.target?.value)}
                  autoFocus
                  className="w-full pl-8 pr-3 py-2 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
              </div>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {productos
                  ?.filter(p => !selectorSearch ||
                    p?.nombre?.toLowerCase()?.includes(selectorSearch?.toLowerCase()) ||
                    (p?.codigo_interno || '')?.toLowerCase()?.includes(selectorSearch?.toLowerCase()) ||
                    (p?.ean || '')?.toLowerCase()?.includes(selectorSearch?.toLowerCase())
                  )
                  ?.slice(0, 30)
                  ?.map(p => (
                    <button key={p?.id} onClick={() => assignProduct(selectorLine, p)}
                      className="w-full text-left px-3 py-2.5 rounded-xl transition-all hover:bg-white/[0.06]"
                      style={{ border: '1px solid transparent' }}>
                      <p className="font-oranienbaum text-white/80 text-sm">{p?.nombre}</p>
                      <p className="font-oranienbaum text-white/35 text-xs">
                        {[p?.codigo_interno, p?.ean]?.filter(Boolean)?.join(' · ') || '—'}
                      </p>
                    </button>
                  ))}
                {productos?.filter(p => !selectorSearch ||
                  p?.nombre?.toLowerCase()?.includes(selectorSearch?.toLowerCase()) ||
                  (p?.codigo_interno || '')?.toLowerCase()?.includes(selectorSearch?.toLowerCase())
                )?.length === 0 && (
                  <p className="font-oranienbaum text-white/30 text-xs text-center py-4">Sin resultados</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── NEW PRODUCT FORM MODAL ─────────────────────────────────────────── */}
      {newProductLine && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.80)' }}>
          <div className="w-full max-w-lg rounded-2xl overflow-hidden max-h-[90vh] flex flex-col" style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.12)' }}>
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
                  <Icon name="PackagePlus" size={15} style={{ color: ACCENT }} />
                </div>
                <h3 className="font-oranienbaum text-white font-semibold text-sm">Crear nuevo producto</h3>
              </div>
              <button onClick={() => { setNewProductLine(null); setNewProductForm({}); }} className="p-1.5 rounded-lg text-white/40 hover:text-white/70">
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-5 space-y-3">
              <div className="p-3 rounded-xl text-xs font-oranienbaum" style={{ background: `${AMBER}08`, border: `1px solid ${AMBER}20`, color: AMBER }}>
                ⚠ Solo se guardarán los datos disponibles en la factura. No se inventarán EAN, categoría, marca ni peso.
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Nombre del producto *</label>
                  <input type="text" value={newProductForm?.nombre || ''}
                    onChange={e => setNewProductForm(p => ({ ...p, nombre: e?.target?.value }))}
                    className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                </div>
                {/* Familia — OBLIGATORIA */}
                <div>
                  <label className="block font-oranienbaum text-xs mb-1">
                    <span className="text-white/40">Familia </span>
                    <span className="font-bold" style={{ color: RED }}>* obligatorio</span>
                  </label>
                  <select
                    value={newProductForm?.familia_id || ''}
                    onChange={e => setNewProductForm(p => ({ ...p, familia_id: e?.target?.value }))}
                    className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum outline-none"
                    style={{ background: '#1A1A1A', border: `1px solid ${!newProductForm?.familia_id ? 'rgba(248,113,113,0.50)' : 'rgba(255,255,255,0.12)'}`, color: 'rgba(255,255,255,0.80)' }}>
                    <option value="">— Seleccionar familia (obligatorio) —</option>
                    {familiasProducto?.map(f => (
                      <option key={f?.id} value={f?.id}>
                        {f?.nombre}{f?.epelsa_family_code ? ` (Epelsa: ${f?.epelsa_family_code})` : ' ⚠ Sin mapeo Epelsa'}
                      </option>
                    ))}
                  </select>
                  {!newProductForm?.familia_id && (
                    <p className="font-oranienbaum text-xs mt-1" style={{ color: RED }}>Debes seleccionar una familia para poder crear el producto.</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-oranienbaum text-white/40 text-xs mb-1">Referencia proveedor</label>
                    <input type="text" value={newProductForm?.referencia_proveedor || ''}
                      onChange={e => setNewProductForm(p => ({ ...p, referencia_proveedor: e?.target?.value }))}
                      className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                  </div>
                  <div>
                    <label className="block font-oranienbaum text-white/40 text-xs mb-1">EAN (si se conoce)</label>
                    <input type="text" value={newProductForm?.ean || ''}
                      onChange={e => setNewProductForm(p => ({ ...p, ean: e?.target?.value }))}
                      className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block font-oranienbaum text-white/40 text-xs mb-1">Precio compra</label>
                    <input type="number" min="0" step="0.01" value={newProductForm?.precio_compra || ''}
                      onChange={e => setNewProductForm(p => ({ ...p, precio_compra: e?.target?.value }))}
                      className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none text-right"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                  </div>
                  <div>
                    <label className="block font-oranienbaum text-white/40 text-xs mb-1">IVA %</label>
                    <input type="number" min="0" max="21" step="1" value={newProductForm?.tipo_iva || 10}
                      onChange={e => setNewProductForm(p => ({ ...p, tipo_iva: e?.target?.value, iva_compra: e?.target?.value }))}
                      className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none text-right"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                  </div>
                  <div>
                    <label className="block font-oranienbaum text-white/40 text-xs mb-1">Margen %</label>
                    <input type="number" min="0" max="99" step="1" value={newProductForm?.margen || 40}
                      onChange={e => setNewProductForm(p => ({ ...p, margen: e?.target?.value }))}
                      className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none text-right"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                  </div>
                </div>
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Unidad de compra</label>
                  <select value={newProductForm?.unidad_compra || 'UNIDAD'}
                    onChange={e => setNewProductForm(p => ({ ...p, unidad_compra: e?.target?.value }))}
                    className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum outline-none"
                    style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.80)' }}>
                    <option value="UNIDAD">Unidad</option>
                    <option value="KG">Kilogramo</option>
                    <option value="LITRO">Litro</option>
                    <option value="CAJA">Caja</option>
                    <option value="PAQUETE">Paquete</option>
                    <option value="PIEZA">Pieza</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-oranienbaum text-white/40 text-xs mb-1">Unidad de inventario</label>
                    <input type="text" value={newProductForm?.unidad_inventario || 'ud'}
                      onChange={e => setNewProductForm(p => ({ ...p, unidad_inventario: e?.target?.value }))}
                      placeholder="ud, kg, litro…"
                      className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                  </div>
                  <div>
                    <label className="block font-oranienbaum text-white/40 text-xs mb-1">Factor conversión (ud/caja)</label>
                    <input type="number" min="0" step="0.001" value={newProductForm?.factor_conversion ?? ''}
                      onChange={e => setNewProductForm(p => ({ ...p, factor_conversion: e?.target?.value }))}
                      placeholder="Ej: 15"
                      className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none text-right"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                  </div>
                </div>
                {/* PVP preview */}
                <PricePreview
                  costeConImpuestos={(() => {
                    const precio = parseFloat(newProductForm?.precio_compra) || 0;
                    const iva = parseFloat(newProductForm?.tipo_iva) || 10;
                    const { costeConImpuestos } = calcularCosteLinea({ precioUnitario: precio, ivaPct: iva });
                    return costeConImpuestos;
                  })()}
                  ivaPct={parseFloat(newProductForm?.tipo_iva) || 10}
                  margen={(parseFloat(newProductForm?.margen) || 40) / 100}
                  reglaRedondeo={reglaRedondeo}
                />
              </div>
            </div>
            <div className="flex gap-3 px-5 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <button onClick={() => { setNewProductLine(null); setNewProductForm({}); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
                Cancelar
              </button>
              <button onClick={saveNewProduct} disabled={!newProductForm?.nombre || !newProductForm?.familia_id}
                className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum font-bold disabled:opacity-40"
                style={{ background: `${ACCENT}20`, border: `1px solid ${ACCENT}40`, color: ACCENT }}>
                Crear producto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MANUAL ENTRY MODAL ───────────────────────────────────────────── */}
      {showManualEntry && (
        <>
          <div className="fixed inset-0 z-[60]" style={{ background: 'rgba(0,0,0,0.70)' }} onClick={() => setShowManualEntry(false)} />
          <div className="fixed z-[61] inset-4 flex items-center justify-center">
            <div className="w-full max-w-2xl max-h-full flex flex-col rounded-2xl overflow-hidden"
              style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.12)' }}
              onClick={e => e?.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 flex-shrink-0"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-2">
                  <Icon name="PenLine" size={16} style={{ color: BLUE }} />
                  <span className="font-oranienbaum font-bold text-white text-sm">Introducir factura manualmente</span>
                </div>
                <button onClick={() => setShowManualEntry(false)} className="p-1.5 rounded-lg text-white/40 hover:text-white/70">
                  <Icon name="X" size={16} />
                </button>
              </div>
              {/* Body */}
              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                {/* Cabecera */}
                <div>
                  <p className="font-oranienbaum text-white/50 text-xs font-semibold uppercase tracking-wider mb-3">Datos de la factura</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-oranienbaum text-white/40 text-xs mb-1">Proveedor</label>
                      <input type="text" value={manualForm?.proveedor || ''} placeholder="Nombre del proveedor"
                        onChange={e => setManualForm(f => ({ ...f, proveedor: e?.target?.value }))}
                        className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                    </div>
                    <div>
                      <label className="block font-oranienbaum text-white/40 text-xs mb-1">CIF / NIF</label>
                      <input type="text" value={manualForm?.cif || ''} placeholder="B12345678"
                        onChange={e => setManualForm(f => ({ ...f, cif: e?.target?.value }))}
                        className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                    </div>
                    <div>
                      <label className="block font-oranienbaum text-white/40 text-xs mb-1">Nº Factura</label>
                      <input type="text" value={manualForm?.numero_factura || ''} placeholder="FAC-2026-001"
                        onChange={e => setManualForm(f => ({ ...f, numero_factura: e?.target?.value }))}
                        className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                    </div>
                    <div>
                      <label className="block font-oranienbaum text-white/40 text-xs mb-1">Forma de pago</label>
                      <input type="text" value={manualForm?.forma_pago || ''} placeholder="Transferencia, 30 días..."
                        onChange={e => setManualForm(f => ({ ...f, forma_pago: e?.target?.value }))}
                        className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                    </div>
                    <div>
                      <label className="block font-oranienbaum text-white/40 text-xs mb-1">Fecha factura</label>
                      <input type="date" value={manualForm?.fecha || ''}
                        onChange={e => setManualForm(f => ({ ...f, fecha: e?.target?.value }))}
                        className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', colorScheme: 'dark' }} />
                    </div>
                    <div>
                      <label className="block font-oranienbaum text-white/40 text-xs mb-1">Fecha vencimiento</label>
                      <input type="date" value={manualForm?.fecha_vencimiento || ''}
                        onChange={e => setManualForm(f => ({ ...f, fecha_vencimiento: e?.target?.value }))}
                        className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', colorScheme: 'dark' }} />
                    </div>
                    <div>
                      <label className="block font-oranienbaum text-white/40 text-xs mb-1">Base imponible €</label>
                      <input type="number" step="0.01" value={manualForm?.base_imponible || ''}
                        onChange={e => setManualForm(f => ({ ...f, base_imponible: e?.target?.value }))}
                        className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none text-right"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                    </div>
                    <div>
                      <label className="block font-oranienbaum text-white/40 text-xs mb-1">IVA €</label>
                      <input type="number" step="0.01" value={manualForm?.iva || ''}
                        onChange={e => setManualForm(f => ({ ...f, iva: e?.target?.value }))}
                        className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none text-right"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                    </div>
                    <div className="col-span-2">
                      <label className="block font-oranienbaum text-white/40 text-xs mb-1">Total €</label>
                      <input type="number" step="0.01" value={manualForm?.total || ''}
                        onChange={e => setManualForm(f => ({ ...f, total: e?.target?.value }))}
                        className="w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none text-right"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }} />
                    </div>
                  </div>
                </div>

                {/* Líneas */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-oranienbaum text-white/50 text-xs font-semibold uppercase tracking-wider">Líneas de producto</p>
                    <button
                      onClick={() => setManualForm(f => ({ ...f, lineas: [...(f?.lineas || []), { descripcion: '', referencia_proveedor: '', ean: '', cantidad: '', unidad: 'UNIDAD', precio_unitario: '', descuento: '', descuento_porcentaje: '', porcentaje_iva: '10' }] }))}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-oranienbaum"
                      style={{ background: `${ACCENT}10`, color: ACCENT, border: `1px solid ${ACCENT}25` }}>
                      <Icon name="Plus" size={11} />Añadir línea
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(manualForm?.lineas || [])?.map((linea, idx) => (
                      <div key={idx} className="p-3 rounded-xl space-y-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        <div className="flex items-center justify-between">
                          <span className="font-oranienbaum text-white/30 text-xs">Línea {idx + 1}</span>
                          {(manualForm?.lineas?.length || 0) > 1 && (
                            <button onClick={() => setManualForm(f => ({ ...f, lineas: f?.lineas?.filter((_, i) => i !== idx) }))}
                              className="text-red-400/60 hover:text-red-400 transition-colors">
                              <Icon name="Trash2" size={12} />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="col-span-2">
                            <input type="text" value={linea?.descripcion || ''} placeholder="Descripción del producto"
                              onChange={e => setManualForm(f => ({ ...f, lineas: f?.lineas?.map((l, i) => i === idx ? { ...l, descripcion: e?.target?.value } : l) }))}
                              className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/80 outline-none"
                              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                          </div>
                          <input type="text" value={linea?.referencia_proveedor || ''} placeholder="Ref. proveedor"
                            onChange={e => setManualForm(f => ({ ...f, lineas: f?.lineas?.map((l, i) => i === idx ? { ...l, referencia_proveedor: e?.target?.value } : l) }))}
                            className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/80 outline-none"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                          <input type="text" value={linea?.ean || ''} placeholder="EAN / Código barras"
                            onChange={e => setManualForm(f => ({ ...f, lineas: f?.lineas?.map((l, i) => i === idx ? { ...l, ean: e?.target?.value } : l) }))}
                            className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/80 outline-none"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                          <input type="number" step="0.001" value={linea?.cantidad || ''} placeholder="Cantidad"
                            onChange={e => setManualForm(f => ({ ...f, lineas: f?.lineas?.map((l, i) => i === idx ? { ...l, cantidad: e?.target?.value } : l) }))}
                            className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/80 outline-none text-right"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                          <input type="number" step="0.01" value={linea?.precio_unitario || ''} placeholder="Precio unitario €"
                            onChange={e => setManualForm(f => ({ ...f, lineas: f?.lineas?.map((l, i) => i === idx ? { ...l, precio_unitario: e?.target?.value } : l) }))}
                            className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/80 outline-none text-right"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                          <input type="number" step="0.01" value={linea?.descuento || ''} placeholder="Descuento €"
                            onChange={e => setManualForm(f => ({ ...f, lineas: f?.lineas?.map((l, i) => i === idx ? { ...l, descuento: e?.target?.value } : l) }))}
                            className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/80 outline-none text-right"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                          <input type="number" step="0.1" value={linea?.porcentaje_iva || '10'} placeholder="IVA %"
                            onChange={e => setManualForm(f => ({ ...f, lineas: f?.lineas?.map((l, i) => i === idx ? { ...l, porcentaje_iva: e?.target?.value } : l) }))}
                            className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/80 outline-none text-right"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Footer */}
              <div className="flex gap-3 px-5 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <button onClick={() => setShowManualEntry(false)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
                  Cancelar
                </button>
                <button onClick={processManualEntry}
                  disabled={!manualForm?.numero_factura && !manualForm?.proveedor}
                  className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum font-bold disabled:opacity-40"
                  style={{ background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }}>
                  Continuar con revisión →
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ImportFacturaWizard;
